// src/admin/AnalyticsPage.jsx
import React from "react";
import {
  Trophy,
  BarChart3,
  Filter,
  Users,
  MousePointerClick,
  Sparkles,
  RefreshCcw,
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  Search,
  X,
  Download,
  Clock,
  CheckCircle2,
  TrendingUp,
  Package,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Layers,
  FileText,
  Target,
  PieChart,
  Timer,
  Building2,
  Briefcase,
  User,
} from "lucide-react";
import { getLenderShortlistPercentage } from "../../api/admin/lenderShortlistPercentage.js";
import { getMonthlyTotalViewsForAdmin } from "../../api/lenderPortal/topProductsViewed.js";
import { Skeleton } from "../../components/ui/Skeleton.jsx";

/**
 * SINGLE PAGE:
 * - First view = 5 big KPI tiles
 * - Click a tile => shows that KPI's own "page" area:
 *    - its own filters/search
 *    - its own sub KPIs
 *    - its own table/blocks
 * - Each KPI keeps its own state (search/range/lender/etc)
 * - No geography/postcodes analytics
 */

/* =========================
   Options
========================= */
const RANGE_PRESETS = [
  { label: "Last 7 days", value: "7d" },
  { label: "Last 30 days", value: "30d" },
  { label: "Last 90 days", value: "90d" },
  { label: "YTD", value: "ytd" },
];

const PRODUCT_TYPES = [
  { label: "All product types", value: "all" },
  { label: "BTL", value: "BTL" },
  { label: "Bridging", value: "Bridging" },
  { label: "Development", value: "Development" },
  { label: "Invoice Finance", value: "IF" },
  { label: "Asset Finance", value: "AF" },
];

// Lender scope options are built from API data in LendersSection
const LENDERS_DEFAULT = [{ label: "All lenders", value: "all" }];

/* =========================
   Helpers
========================= */
function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}
function formatInt(n) {
  const x = Number(n) || 0;
  return x.toLocaleString("en-GB");
}
function formatPct(p, digits = 0) {
  const x = Number(p) || 0;
  return `${(x * 100).toFixed(digits)}%`;
}
function formatDays(n) {
  const x = Number(n) || 0;
  return `${x.toFixed(1)}d`;
}
function formatGbp(n) {
  const x = Number(n) || 0;
  return x.toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  });
}
function safeDiv(a, b) {
  const A = Number(a) || 0;
  const B = Number(b) || 0;
  if (!B) return 0;
  return A / B;
}

/** Convert range preset (7d, 30d, 90d, ytd) to from_date and to_date (YYYY-MM-DD) */
function rangeToDates(range) {
  const to = new Date();
  const to_date = to.toISOString().slice(0, 10);
  let from = new Date();
  if (range === "ytd") {
    from = new Date(to.getFullYear(), 0, 1);
  } else {
    const days = { "7d": 7, "30d": 30, "90d": 90 }[range] ?? 30;
    from.setDate(from.getDate() - days);
  }
  const from_date = from.toISOString().slice(0, 10);
  return { from_date, to_date };
}
function downloadCsv(filename, rows) {
  const esc = (v) => {
    const s = String(v ?? "");
    if (s.includes('"') || s.includes(",") || s.includes("\n"))
      return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/* =========================
   SELLABLE LENDER SCORE
========================= */
function lenderScore(l) {
  const approval = clamp(l.approvalRate, 0, 1) * 35;
  const ttc = (1 - clamp((l.medianTtcDays - 12) / 18, 0, 1)) * 20; // faster better
  const volume = clamp(l.volume / 300, 0, 1) * 10;
  const sla = clamp(l.responseSla24h, 0, 1) * 15;
  const engagement = clamp(l.engagementScore, 0, 1) * 10;
  const satisfaction = clamp((l.satisfaction - 3) / 2, 0, 1) * 10;
  return approval + ttc + volume + sla + engagement + satisfaction;
}

/* =========================
   Load Analytics from localStorage (Frontend-only tracking)
========================= */
async function loadAnalytics({ range, lenderId, productType }) {
  await new Promise((r) => setTimeout(r, 220));

  // Get sourcing analytics from localStorage
  const sourcingData = getSourcingAnalyticsFromLocalStorage(range);

  const snapshot = {
    clicks: 18240,
    sourcingRuns: sourcingData.summary.totalSearches,
    appsStarted: 820,
    appsCompleted: 214,
    approvalRate: 0.41,
    medianTtcDays: 18.4,
  };

  const clicks = {
    borrowerClicks: 1204,
    lenderClicks: 312,
    signups: 432,
    signins: 1905,
    clickToSignup: 0.28,
    topFilters: [],
  };

  const funnel = [
    { stage: "Sourcing", value: 1000, avgDays: 0.3 },
    { stage: "DIP", value: 620, avgDays: 2.1 },
    { stage: "Offer", value: 360, avgDays: 8.7 },
    { stage: "Completion", value: 214, avgDays: 7.3 },
  ];

  // Lender Intelligence: from API dynesourcerapi.shortlist_api.lender_shortlist_percentage
  const { from_date, to_date } = rangeToDates(range || "30d");
  let lenders = [];
  try {
    const shortlistRes = await getLenderShortlistPercentage({
      from_date,
      to_date,
      statuses: ["Pending", "Approved", "Rejected"],
      page: 1,
      page_size: 200,
    });
    lenders = (shortlistRes.data || []).map((row) => ({
      id: row.lender_user || row.lender_name || String(Math.random()),
      lender: row.lender_name || row.lender_user || "—",
      volume: null,
      approvalRate: null,
      medianTtcDays: null,
      productViews: null,
      shortlistRate: (Number(row.shortlist_percentage) || 0) / 100,
      shortlisted_distinct_products: row.shortlisted_distinct_products ?? 0,
      portfolio: { activeProducts: Number(row.total_products) || 0 },
      productCategories: [],
    }));

    // Total views per lender: same API as lender portal (get_monthly_total_views), with for_admin=1
    let viewsByLender = {};
    try {
      const year = new Date().getFullYear();
      const rangeToTimeframe = (r) => {
        if (r === "ytd") return "all";
        const days = { "7d": 7, "30d": 30, "90d": 90 }[r || "30d"] ?? 30;
        return days <= 7 ? "all" : days <= 30 ? "last_3_months" : "last_6_months";
      };
      viewsByLender = await getMonthlyTotalViewsForAdmin({
        timeframe: rangeToTimeframe(range),
        year,
      });
    } catch (_) {
      // non-blocking
    }
    if (Object.keys(viewsByLender).length > 0 && !viewsByLender._single) {
      lenders = lenders.map((l) => {
        const key = (l.id || "").toString().trim().toLowerCase();
        const total = key ? viewsByLender[key] : null;
        return { ...l, productViews: total != null ? total : l.productViews };
      });
    }
  } catch (err) {
    console.error("Lender shortlist percentage:", err);
  }

  const topProducts = [
    { name: "Paragon BTL 2Y Fixed 75% LTV", lender: "Paragon", views: 1820, shortlistRate: 0.31, applyRate: 0.14, approvalRate: 0.53 },
    { name: "Aldermore BTL 5Y Fixed 70% LTV", lender: "Aldermore", views: 1490, shortlistRate: 0.28, applyRate: 0.11, approvalRate: 0.48 },
    { name: "Precise Bridging 12m 70% LTV", lender: "Precise", views: 1185, shortlistRate: 0.22, applyRate: 0.09, approvalRate: 0.41 },
    { name: "Kent Reliance BTL 2Y Var 75% LTV", lender: "Kent Reliance", views: 1042, shortlistRate: 0.19, applyRate: 0.07, approvalRate: 0.38 },
    { name: "Vida Complex BTL 2Y Fixed 65% LTV", lender: "Vida", views: 980, shortlistRate: 0.17, applyRate: 0.06, approvalRate: 0.35 },
  ];

  const borrowers = {
    active: 1820,
    dormant: 640,
    newSignups: clicks.signups,
    activated: 920,
    repeatUsers: 610,
    satisfactionAvg: 4.3,
  };

  const lendersScoped = lenderId === "all" ? lenders : lenders.filter((l) => l.id === lenderId);

  return {
    snapshot,
    clicks,
    funnel,
    lenders: lendersScoped,
    topProducts,
    borrowers,
    sourcingFilters: sourcingData.summary.filterUsage, // Real data from localStorage
  };
}

/**
 * Get sourcing analytics from localStorage
 */
function getSourcingAnalyticsFromLocalStorage(range) {
  try {
    const data = localStorage.getItem("ds_sourcing_analytics");

    // If no data exists, return empty
    if (!data) {
      console.log("No sourcing analytics data found in localStorage");
      return {
        events: [],
        summary: {
          totalSearches: 0,
          averageResults: 0,
          filterUsage: {},
        },
      };
    }

    const analytics = JSON.parse(data);
    let events = analytics.events || [];

    // Filter by date range
    if (range && range !== "all") {
      const daysMap = { "7d": 7, "30d": 30, "90d": 90, "365d": 365 };
      const days = daysMap[range] || 30;
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);

      events = events.filter(e => {
        const eventDate = new Date(e.timestamp);
        return eventDate >= cutoffDate;
      });
    }

    // Generate summary with proper filter formatting
    // Map backend filter keys to frontend display keys
    const filterKeyMap = {
      // Loan purpose
      loan_purpose: "loanPurpose",
      // LTV
      ity_percent: "ltv",
      // EPC Rating
      epc_rating_green: "epcGreen",
      // Product term
      product_termsyears: "productTerm",
      // Repayment type (if tracked separately)
      repayment_type: "repaymentType",
      // Mortgage term (if tracked separately)
      mortgage_term: "mortgageTerm",
      mortgage_term_years: "mortgageTerm",
      // Note: mortgage_term_years might be in filters or as separate field
      // Product type
      product_type: "productType",
      // Borrower type
      borrower_type: "borrowerType",
      // Home owner
      home_owner: "homeowner",
      // BTL experience - note: backend uses btl_experince_year (typo in backend)
      btl_experience: "btlExp",
      btl_experince_year: "btlExp",
      // Credit worthiness
      credit_worthiness: "credit",
      // Tenancy type
      tenancy_type: "tenancyType",
      // Tenure - note: backend uses fh_or_lh
      tenure: "tenure",
      fh_or_lh: "tenure",
      // Ex local authority
      ex_local_authority: "exAuthority",
      // Deposit origination
      deposit_originantion: "depositOrigination",
      // Day 1 remortgage
      day_1_remortgage: "day1Remortgage",
    };

    // Format filter values for display
    const formatFilterValue = (key, value) => {
      const strValue = String(value);
      
      // Format LTV as percentage
      if (key === "ity_percent" || key === "ltv") {
        return `${strValue}%`;
      }
      
      // Format product term
      if (key === "product_termsyears" || key === "productTerm") {
        if (strValue === "0.5") return "0.5";
        if (strValue === "Lifetime" || strValue === "lifetime") return "Lifetime";
        return `${strValue}${strValue.includes("yrs") || strValue.includes("yr") ? "" : " yrs"}`;
      }
      
      // Format mortgage term
      if (key === "mortgage_term" || key === "mortgage_term_years") {
        return `${strValue}${strValue.includes("yrs") || strValue.includes("yr") ? "" : " yrs"}`;
      }
      
      // Format BTL experience
      if (key === "btl_experince_year" || key === "btl_experience") {
        if (strValue === "0" || strValue === "No") return "No";
        if (strValue === "1-5") return "1-5";
        if (strValue === "5+" || strValue === "5+ years") return "5+";
        return strValue;
      }
      
      // Format tenure (fh_or_lh)
      if (key === "fh_or_lh" || key === "tenure") {
        if (strValue === "FREEHOLD" || strValue === "Freehold" || strValue === "F/H") return "FREEHOLD";
        if (strValue === "LEASEHOLD" || strValue === "Leasehold" || strValue === "L/H") return "LEASEHOLD";
        return strValue.toUpperCase();
      }
      
      // Format product type (capitalize properly)
      if (key === "product_type" || key === "productType") {
        // Handle common product type values
        const productTypeMap = {
          "fixed": "Fixed",
          "variable": "Variable",
          "tracker": "Tracker",
          "discounted": "Discounted",
          "offset": "Offset",
        };
        const lowerValue = strValue.toLowerCase();
        return productTypeMap[lowerValue] || strValue.charAt(0).toUpperCase() + strValue.slice(1).toLowerCase();
      }
      
      // Format repayment type (capitalize properly)
      if (key === "repayment_type") {
        if (strValue.toLowerCase() === "interest only" || strValue.toLowerCase() === "interest-only") return "Interest only";
        if (strValue.toLowerCase() === "repayment") return "Repayment";
        return strValue.charAt(0).toUpperCase() + strValue.slice(1);
      }
      
      // Format EPC rating
      if (key === "epc_rating_green" || key === "epcGreen") {
        if (strValue === "Yes" || strValue === "yes" || strValue === "1" || strValue === "true") return "A-C";
        if (strValue === "No" || strValue === "no" || strValue === "0" || strValue === "false") return "D-F";
        return strValue;
      }
      
      // Capitalize first letter for other values
      return strValue.charAt(0).toUpperCase() + strValue.slice(1);
    };

    const filterUsage = {};
    let totalResults = 0;

    events.forEach(event => {
      if (event.filters) {
        Object.entries(event.filters).forEach(([key, value]) => {
          // Map backend key to frontend key
          const displayKey = filterKeyMap[key] || key;
          
          if (!filterUsage[displayKey]) {
            filterUsage[displayKey] = {};
          }
          
          // Format value for display
          const formattedValue = formatFilterValue(key, value);
          
          // Convert value to string for consistent keys
          filterUsage[displayKey][formattedValue] = (filterUsage[displayKey][formattedValue] || 0) + 1;
        });
      }

      if (event.results && event.results.total_count) {
        totalResults += event.results.total_count;
      }
    });

    console.log("Sourcing analytics loaded:", {
      totalEvents: events.length,
      filterKeys: Object.keys(filterUsage),
      sampleFilters: Object.keys(filterUsage).slice(0, 5),
      productTypeData: filterUsage.productType, // Specifically log productType data
      fullFilterUsage: filterUsage, // Log full data for debugging
    });

    return {
      events,
      summary: {
        totalSearches: events.length,
        averageResults: events.length > 0 ? Math.round(totalResults / events.length) : 0,
        filterUsage, // Only real data, no mock fallback
      },
    };
  } catch (error) {
    console.error("Failed to load sourcing analytics:", error);
    return {
      events: [],
      summary: {
        totalSearches: 0,
        averageResults: 0,
        filterUsage: {},
      },
    };
  }
}

/* =========================
   KPI Definitions
========================= */
const KPI = {
  HOME: "home",
  LENDERS: "lenders",
  APPLICATIONS: "applications",
  SOURCING: "sourcing",
  BORROWERS: "borrowers",
  CLICKS: "clicks",
};

const KPI_META = {
  [KPI.LENDERS]: { title: "Lender intelligence", icon: Trophy, subtitle: "Sellable benchmarking: approval, TTC, SLA, engagement, demand & portfolio health." },
  [KPI.APPLICATIONS]: { title: "Application", icon: BarChart3, subtitle: "Funnel, conversion, time-in-stage and outcomes." },
  [KPI.SOURCING]: { title: "Sourcing", icon: Filter, subtitle: "Most used filters + demand signals + sourcing runs." },
  [KPI.BORROWERS]: { title: "Borrower", icon: Users, subtitle: "Activation, retention, behaviour (no geography)." },
  [KPI.CLICKS]: { title: "Clicks", icon: MousePointerClick, subtitle: "Homepage role selection + signup/signin funnel." },
};

/* =========================
   UI atoms
========================= */
function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-8 w-8 rounded-full bg-sky-50 flex items-center justify-center">
        <Icon className="h-4 w-4 text-sky-500" />
      </div>
      <div>
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        <p className="text-xs text-slate-500">{subtitle}</p>
      </div>
    </div>
  );
}
function ControlSelect({ label, value, onChange, options }) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-slate-500 font-medium">{label}</span>
      <div className="relative">
        <select
          className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-3 py-2 pr-9 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-200"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      </div>
    </label>
  );
}
function SearchInput({ label, value, onChange, placeholder }) {
  return (
    <label className="text-xs block">
      <span className="text-slate-500 font-medium">{label}</span>
      <div className="relative mt-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-9 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-200"
          placeholder={placeholder}
        />
        {value ? (
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            title="Clear"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    </label>
  );
}
// eslint-disable-next-line no-unused-vars
function KpiCard({ icon: Icon, label, value, subtitle }) {
  return (
    <div className="group bg-white rounded-2xl border border-slate-200 p-4 transition-all duration-300 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 hover:-translate-y-1 cursor-pointer">
      <div className="flex items-start justify-between">
        <div className="h-8 w-8 rounded-xl bg-sky-50 flex items-center justify-center transition-all duration-300 group-hover:bg-sky-100 group-hover:scale-110">
          <Icon className="h-4 w-4 text-sky-600 transition-all duration-300 group-hover:text-sky-700 group-hover:scale-110" />
        </div>
      </div>
      <div className="mt-3 text-[11px] font-medium text-slate-500 transition-colors duration-300 group-hover:text-slate-600">{label}</div>
      <div className="mt-1 text-lg font-semibold text-slate-900 transition-all duration-300 group-hover:text-sky-600 group-hover:scale-105">{value}</div>
      {subtitle ? <div className="mt-1 text-[10px] text-slate-400 transition-colors duration-300 group-hover:text-slate-500">{subtitle}</div> : null}
    </div>
  );
}
function Badge({ children, tone }) {
  const cls =
    tone === "good"
      ? "bg-emerald-50 text-emerald-700 border-emerald-100"
      : tone === "warn"
        ? "bg-amber-50 text-amber-700 border-amber-100"
        : tone === "bad"
          ? "bg-rose-50 text-rose-700 border-rose-100"
          : "bg-slate-50 text-slate-700 border-slate-200";
  return <span className={`inline-flex items-center rounded-full border px-2 py-1 text-[11px] ${cls}`}>{children}</span>;
}
function Th({ children, onClick, active, dir, right, className = "" }) {
  return (
    <th
      onClick={onClick}
      className={`px-4 py-3 font-semibold text-center text-slate-700 transition-all duration-200 ${onClick ? "cursor-pointer select-none hover:bg-sky-100/50 hover:text-sky-700" : ""
        } ${active ? "bg-sky-100/70 text-sky-800" : ""} ${className}`}
    >
      <span className="inline-flex items-center justify-center gap-2">
        {children}
        {active ? (
          dir === "asc" ? (
            <ArrowUpRight className="h-4 w-4 text-sky-600" />
          ) : (
            <ArrowDownRight className="h-4 w-4 text-sky-600" />
          )
        ) : onClick ? (
          <div className="h-4 w-4 opacity-0 group-hover:opacity-30 transition-opacity">
            <ArrowUpRight className="h-4 w-4 text-slate-400" />
          </div>
        ) : null}
      </span>
    </th>
  );
}
function BigTile({ icon: Icon, title, subtitle, primary, secondary, loading: tileLoading, onClick, gradient = "from-sky-50 to-blue-50", iconBg = "bg-sky-100", iconColor = "text-sky-600", borderColor = "border-sky-200", hoverBorder = "hover:border-sky-300", hoverShadow = "hover:shadow-sky-200" }) {
  return (
    <button
      onClick={onClick}
      className={`group text-left bg-gradient-to-br ${gradient} rounded-2xl border-2 ${borderColor} p-6 transition-all duration-300 ${hoverBorder} hover:shadow-xl ${hoverShadow} hover:-translate-y-2 hover:scale-[1.02] relative overflow-hidden`}
      style={{ minHeight: 200 }}
    >
      {/* Animated background gradient overlay */}
      <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />

      <div className="relative z-10">
        <div className="flex items-start justify-between gap-2 mb-4">
          <div className={`h-12 w-12 rounded-2xl ${iconBg} flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 shadow-sm`}>
            <Icon className={`h-6 w-6 ${iconColor} transition-all duration-300 group-hover:scale-110`} />
          </div>
          <span className="inline-flex items-center rounded-full border-2 border-white/50 px-3 py-1.5 text-[11px] font-semibold bg-white/80 backdrop-blur-sm text-slate-700 shadow-sm transition-all duration-300 group-hover:bg-white group-hover:scale-110 group-hover:shadow-md">
            Open →
          </span>
        </div>
        <div className="mt-2 mb-2">
          <div className="text-lg font-bold text-slate-900 transition-all duration-300 group-hover:text-slate-950 group-hover:scale-105">{title}</div>
          <div className="mt-2 text-xs text-slate-600 line-clamp-2 leading-relaxed transition-colors duration-300 group-hover:text-slate-700">{subtitle}</div>
        </div>
        <div className="mt-6 pt-4 border-t border-white/50">
          {tileLoading ? (
            <>
              <Skeleton height="1.75rem" width="70%" className="rounded-lg bg-white/60" />
              <Skeleton height="0.875rem" width="50%" className="mt-2 rounded-lg bg-white/60" />
            </>
          ) : (
            <>
              <div className="text-2xl font-bold text-slate-900 transition-all duration-300 group-hover:scale-105 group-hover:text-slate-950">{primary}</div>
              <div className="mt-2 text-xs font-medium text-slate-600 transition-colors duration-300 group-hover:text-slate-700">{secondary}</div>
            </>
          )}
        </div>
      </div>

      {/* Shine effect on hover */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
      </div>
    </button>
  );
}

/* =========================
   Page
========================= */
export default function AnalyticsPage() {
  const [active, setActive] = React.useState(KPI.HOME);

  // IMPORTANT: each KPI has its own "page state"
  const [state, setState] = React.useState({
    lenders: { range: "30d", lenderId: "all", productCategory: "all", q: "", sort: { key: "shortlistRate", dir: "desc" } },
    applications: { range: "30d", productType: "all", q: "" },
    sourcing: { range: "30d", productType: "all", q: "", sort: { key: "usage", dir: "desc" } },
    borrowers: { range: "30d", q: "" },
    clicks: { range: "30d", q: "" },
  });

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [data, setData] = React.useState(null);

  const refresh = React.useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      // Use current KPI scope to query the backend; simplest: use lenders page controls as global scope
      const s = state.lenders;
      const res = await loadAnalytics({ range: s.range, lenderId: s.lenderId, productType: state.sourcing.productType });
      setData(res);
    } catch (e) {
      setError("Failed to load analytics. Check your API + auth.");
    } finally {
      setLoading(false);
    }
  }, [state.lenders, state.sourcing.productType]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  // HOME tile stats (quick overview only)
  const tileStats = React.useMemo(() => {
    const s = data?.snapshot;
    const c = data?.clicks;
    const lendersCount = data?.lenders?.length || 0;

    return {
      lenders: {
        primary: lendersCount ? `${formatInt(lendersCount)} lenders` : "—",
        secondary: lendersCount && data.lenders.some((x) => x.shortlistRate != null)
          ? `Top shortlist: ${(Math.max(...data.lenders.map((x) => (x.shortlistRate || 0) * 100))).toFixed(1)}%`
          : "—",
      },
      applications: {
        primary: s ? `${formatInt(s.appsStarted)} started` : "—",
        secondary: s ? `${formatInt(s.appsCompleted)} completed · ${formatPct(s.approvalRate)} approval` : "—",
      },
      sourcing: {
        primary: s ? `${formatInt(s.sourcingRuns)} runs` : "—",
        secondary: data?.clicks?.topFilters?.[0] ? `${formatInt(data.clicks.topFilters[0].value)} top-filter uses` : "—",
      },
      borrowers: {
        primary: data ? `${formatInt(data.borrowers.active)} active` : "—",
        secondary: data ? `${formatInt(data.borrowers.dormant)} dormant · ${formatInt(data.borrowers.newSignups)} signups` : "—",
      },
      clicks: {
        primary: s ? `${formatInt(s.clicks)} total` : "—",
        secondary: c ? `Borrower: ${formatInt(c.borrowerClicks)} · Lender: ${formatInt(c.lenderClicks)}` : "—",
      },
    };
  }, [data]);

  const setKpiState = (kpiKey, patch) => {
    setState((prev) => ({ ...prev, [kpiKey]: { ...prev[kpiKey], ...patch } }));
  };

  /* =========================
     Render: Common header
  ========================= */
  return (
    <div className="space-y-6">
      {/* Global header */}
      <section className="bg-white rounded-2xl border border-slate-200 px-4 py-3 flex items-start md:items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-sky-500" />
            Analytics
          </h1>

        </div>

        <div className="flex items-center gap-2">
          {active !== KPI.HOME ? (
            <button
              onClick={() => setActive(KPI.HOME)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium border border-slate-200 text-slate-700 hover:bg-slate-50"
            >
              <ArrowLeft className="h-4 w-4 text-slate-500" />
              Back
            </button>
          ) : null}

          <button
            onClick={refresh}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium border border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            <RefreshCcw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </section>

      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700">
          {error}
        </div>
      ) : null}

      {/* =========================
         HOME VIEW
      ========================= */}
      {active === KPI.HOME ? (
        <section className="bg-white rounded-2xl border border-slate-200 p-4">
          <div className="text-sm font-semibold text-slate-900">First view (5 KPI tiles)</div>
          <div className="text-xs text-slate-500 mt-1">Click a KPI to open its detailed section.</div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-5">
            <BigTile
              icon={Trophy}
              title="Lender intelligence"
              subtitle={KPI_META[KPI.LENDERS].subtitle}
              primary={tileStats.lenders.primary}
              secondary={tileStats.lenders.secondary}
              loading={loading}
              onClick={() => setActive(KPI.LENDERS)}
              gradient="from-amber-50 via-yellow-50 to-amber-50"
              iconBg="bg-gradient-to-br from-amber-100 to-yellow-100"
              iconColor="text-amber-600"
              borderColor="border-amber-200"
              hoverBorder="hover:border-amber-400"
              hoverShadow="hover:shadow-amber-200"
            />
            <BigTile
              icon={BarChart3}
              title="Application"
              subtitle={KPI_META[KPI.APPLICATIONS].subtitle}
              primary={tileStats.applications.primary}
              secondary={tileStats.applications.secondary}
              loading={loading}
              onClick={() => setActive(KPI.APPLICATIONS)}
              gradient="from-blue-50 via-indigo-50 to-blue-50"
              iconBg="bg-gradient-to-br from-blue-100 to-indigo-100"
              iconColor="text-blue-600"
              borderColor="border-blue-200"
              hoverBorder="hover:border-blue-400"
              hoverShadow="hover:shadow-blue-200"
            />
            <BigTile
              icon={Filter}
              title="Sourcing"
              subtitle={KPI_META[KPI.SOURCING].subtitle}
              primary={tileStats.sourcing.primary}
              secondary={tileStats.sourcing.secondary}
              loading={loading}
              onClick={() => setActive(KPI.SOURCING)}
              gradient="from-emerald-50 via-teal-50 to-emerald-50"
              iconBg="bg-gradient-to-br from-emerald-100 to-teal-100"
              iconColor="text-emerald-600"
              borderColor="border-emerald-200"
              hoverBorder="hover:border-emerald-400"
              hoverShadow="hover:shadow-emerald-200"
            />
            <BigTile
              icon={Users}
              title="Borrower"
              subtitle={KPI_META[KPI.BORROWERS].subtitle}
              primary={tileStats.borrowers.primary}
              secondary={tileStats.borrowers.secondary}
              loading={loading}
              onClick={() => setActive(KPI.BORROWERS)}
              gradient="from-purple-50 via-pink-50 to-purple-50"
              iconBg="bg-gradient-to-br from-purple-100 to-pink-100"
              iconColor="text-purple-600"
              borderColor="border-purple-200"
              hoverBorder="hover:border-purple-400"
              hoverShadow="hover:shadow-purple-200"
            />
            <BigTile
              icon={MousePointerClick}
              title="Clicks"
              subtitle={KPI_META[KPI.CLICKS].subtitle}
              primary={tileStats.clicks.primary}
              secondary={tileStats.clicks.secondary}
              loading={loading}
              onClick={() => setActive(KPI.CLICKS)}
              gradient="from-cyan-50 via-sky-50 to-cyan-50"
              iconBg="bg-gradient-to-br from-cyan-100 to-sky-100"
              iconColor="text-cyan-600"
              borderColor="border-cyan-200"
              hoverBorder="hover:border-cyan-400"
              hoverShadow="hover:shadow-cyan-200"
            />
          </div>
        </section>
      ) : null}

      {/* =========================
         KPI SECTION: LENDERS
      ========================= */}
      {active === KPI.LENDERS ? (
        <LendersSection
          data={data}
          loading={loading}
          s={state.lenders}
          setS={(patch) => setKpiState("lenders", patch)}
        />
      ) : null}

      {/* =========================
         KPI SECTION: APPLICATIONS
      ========================= */}
      {active === KPI.APPLICATIONS ? (
        <ApplicationsSection
          data={data}
          loading={loading}
          s={state.applications}
          setS={(patch) => setKpiState("applications", patch)}
        />
      ) : null}

      {/* =========================
         KPI SECTION: SOURCING
      ========================= */}
      {active === KPI.SOURCING ? (
        <SourcingSection
          data={data}
          loading={loading}
          s={state.sourcing}
          setS={(patch) => setKpiState("sourcing", patch)}
        />
      ) : null}

      {/* =========================
         KPI SECTION: BORROWERS
      ========================= */}
      {active === KPI.BORROWERS ? (
        <BorrowersSection
          data={data}
          loading={loading}
          s={state.borrowers}
          setS={(patch) => setKpiState("borrowers", patch)}
        />
      ) : null}

      {/* =========================
         KPI SECTION: CLICKS
      ========================= */}
      {active === KPI.CLICKS ? (
        <ClicksSection
          data={data}
          loading={loading}
          s={state.clicks}
          setS={(patch) => setKpiState("clicks", patch)}
        />
      ) : null}
    </div>
  );
}

/* =========================
   KPI Sections (each has its own toolbar + sub KPIs)
========================= */

function LendersSection({ data, loading, s, setS }) {
  const [selected, setSelected] = React.useState(null);

  const rows = React.useMemo(() => {
    const list = (data?.lenders || []).map((l) => ({ ...l, score: lenderScore(l) }));

    // Filter by product category
    let filtered = list;
    if (s.productCategory && s.productCategory !== "all") {
      filtered = list.filter((l) => {
        const categories = l.productCategories || [];
        return categories.includes(s.productCategory);
      });
    }

    // Filter by search query
    const q = (s.q || "").trim().toLowerCase();
    if (q) {
      filtered = filtered.filter((l) => l.lender.toLowerCase().includes(q));
    }

    const { key, dir } = s.sort || { key: "lender", dir: "asc" };
    filtered.sort((a, b) => {
      let aVal, bVal;
      if (key === "portfolio.activeProducts") {
        aVal = a.portfolio?.activeProducts || 0;
        bVal = b.portfolio?.activeProducts || 0;
      } else if (key === "lender") {
        aVal = (a.lender || "").toLowerCase();
        bVal = (b.lender || "").toLowerCase();
        return dir === "asc" ? (aVal < bVal ? -1 : aVal > bVal ? 1 : 0) : (aVal > bVal ? -1 : aVal < bVal ? 1 : 0);
      } else {
        aVal = Number(a[key]) || 0;
        bVal = Number(b[key]) || 0;
      }
      return dir === "asc" ? aVal - bVal : bVal - aVal;
    });
    return filtered;
  }, [data, s.q, s.sort, s.productCategory]);

  // eslint-disable-next-line no-unused-vars
  const topScore = rows.length ? Math.max(...rows.map((r) => r.score)) : 0;
  // eslint-disable-next-line no-unused-vars
  const topApproval = rows.length ? Math.max(...rows.map((r) => r.approvalRate)) : 0;
  // eslint-disable-next-line no-unused-vars
  const bestTtc = rows.length ? Math.min(...rows.map((r) => r.medianTtcDays)) : 0;
  // eslint-disable-next-line no-unused-vars
  const totalViews = rows.reduce((a, r) => a + (Number(r.productViews) || 0), 0);
  // eslint-disable-next-line no-unused-vars
  const avgEngagement = rows.length ? rows.reduce((a, r) => a + (r.engagementScore || 0), 0) / rows.length : 0;

  const toggleSort = (key) =>
    setS({
      sort: (s.sort?.key === key ? { key, dir: s.sort.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }),
    });

  const exportCsv = () => {
    const out = [
      ["Lender", "Avg Time to Cash (days)", "Total Products", "Total Applications", "Total Views", "Shortlisted Product %"],
      ...rows.map((l) => [
        l.lender,
        l.medianTtcDays || 0,
        l.portfolio?.activeProducts || 0,
        l.volume || 0,
        l.productViews || 0,
        (l.shortlistRate || 0) * 100,
      ]),
    ];
    downloadCsv(`lender_intel_${s.range}.csv`, out);
  };

  return (
    <div className="space-y-5">
      {/* KPI header + KPI-toolbar (unique) */}
      <section className="bg-gradient-to-br from-white via-sky-50/30 to-white rounded-2xl border border-sky-200/50 shadow-sm p-5">
        <div className="flex items-start justify-between gap-3">
          <SectionHeader icon={Trophy} title="Lender intelligence" subtitle="Everything lenders pay for: benchmarking + demand + SLA + portfolio optimisation." />
          <button
            onClick={exportCsv}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-medium bg-gradient-to-r from-sky-500 to-blue-600 text-white hover:from-sky-600 hover:to-blue-700 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 xl:grid-cols-12 gap-4">
          <div className="xl:col-span-3">
            <ControlSelect label="Date range" value={s.range} onChange={(v) => setS({ range: v })} options={RANGE_PRESETS} />
          </div>
          <div className="xl:col-span-3">
            <ControlSelect label="Product category" value={s.productCategory || "all"} onChange={(v) => setS({ productCategory: v })} options={[
              { label: "All Categories", value: "all" },
              { label: "BTL", value: "BTL" },
              { label: "IF", value: "IF" },
              { label: "SC", value: "SC" },
              { label: "C (Commercial)", value: "C" },
              { label: "Dev (Development)", value: "Dev" },
            ]} />
          </div>
          <div className="xl:col-span-3">
            <ControlSelect
              label="Scope (lender)"
              value={s.lenderId}
              onChange={(v) => setS({ lenderId: v })}
              options={[
                ...LENDERS_DEFAULT,
                ...(data?.lenders || []).map((l) => ({ label: l.lender, value: l.id })),
              ]}
            />
          </div>
          <div className="xl:col-span-3">
            <SearchInput label="Search lender" value={s.q} onChange={(v) => setS({ q: v })} placeholder="Type lender name…" />
          </div>
        </div>
      </section>

      {/* Lender Analytics Table - Detailed view for admin to sell to lenders */}
      <section className="bg-white rounded-2xl border border-slate-200 shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">All Lenders Analytics</h2>
            <p className="text-xs text-slate-500 mt-1.5">Comprehensive lender performance metrics for admin insights and selling analytics to lenders</p>
          </div>
          <button
            onClick={exportCsv}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-medium bg-gradient-to-r from-sky-500 to-blue-600 text-white hover:from-sky-600 hover:to-blue-700 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        </div>

        <div className="mt-4 overflow-auto rounded-xl border border-slate-200 shadow-inner">
          <table className="min-w-full text-sm">
            <thead className="bg-gradient-to-r from-sky-50 via-blue-50 to-sky-50">
              <tr>
                <Th onClick={() => toggleSort("lender")} active={s.sort?.key === "lender"} dir={s.sort?.dir} className="sticky left-0 z-10 bg-gradient-to-r from-sky-50 via-blue-50 to-sky-50">
                  Lender
                </Th>
                <Th onClick={() => toggleSort("medianTtcDays")} active={s.sort?.key === "medianTtcDays"} dir={s.sort?.dir}>
                  Avg Time to Cash
                </Th>
                <Th onClick={() => toggleSort("portfolio.activeProducts")} active={s.sort?.key === "portfolio.activeProducts"} dir={s.sort?.dir}>
                  Total Products
                </Th>
                <Th onClick={() => toggleSort("volume")} active={s.sort?.key === "volume"} dir={s.sort?.dir}>
                  Total Applications
                </Th>
                <Th onClick={() => toggleSort("productViews")} active={s.sort?.key === "productViews"} dir={s.sort?.dir}>
                  Total Views
                </Th>
                <Th onClick={() => toggleSort("shortlistRate")} active={s.sort?.key === "shortlistRate"} dir={s.sort?.dir}>
                  Shortlisted Product %
                </Th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {rows.map((l, idx) => (
                <tr
                  key={l.id}
                  className="group hover:bg-gradient-to-r hover:from-sky-50/80 hover:via-blue-50/50 hover:to-sky-50/80 transition-all duration-300 cursor-pointer border-l-4 border-transparent hover:border-sky-400"
                  onClick={() => setSelected(selected?.id === l.id ? null : l)}
                  style={{ animationDelay: `${idx * 50}ms` }}
                >
                  <td className="px-4 py-4 font-semibold text-slate-900 sticky left-0 z-10 bg-white group-hover:bg-gradient-to-r group-hover:from-sky-50/80 group-hover:via-blue-50/50 group-hover:to-sky-50/80 transition-all duration-300 text-center">
                    <span className="text-base">{l.lender}</span>
                  </td>
                  <td className="px-4 py-4 text-center text-slate-700">
                    <div className="flex items-center justify-center gap-2 group-hover:scale-105 transition-transform duration-200">
                      <Clock className="h-4 w-4 text-sky-500 group-hover:text-sky-600 transition-colors" />
                      <span className="font-semibold text-slate-900">{l.medianTtcDays != null ? formatDays(l.medianTtcDays) : "—"}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center text-slate-700">
                    <div className="flex items-center justify-center gap-2 group-hover:scale-105 transition-transform duration-200">
                      <Package className="h-4 w-4 text-emerald-500 group-hover:text-emerald-600 transition-colors" />
                      <span className="font-semibold text-slate-900">{formatInt(l.portfolio?.activeProducts ?? 0)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center text-slate-700">
                    <div className="flex items-center justify-center gap-2 group-hover:scale-105 transition-transform duration-200">
                      <FileText className="h-4 w-4 text-amber-500 group-hover:text-amber-600 transition-colors" />
                      <span className="font-semibold text-slate-900">{l.volume != null ? formatInt(l.volume) : "—"}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center text-slate-700">
                    <div className="flex items-center justify-center gap-2 group-hover:scale-105 transition-transform duration-200">
                      <TrendingUp className="h-4 w-4 text-purple-500 group-hover:text-purple-600 transition-colors" />
                      <span className="font-semibold text-slate-900">{l.productViews != null ? formatInt(l.productViews) : "—"}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <div className="flex items-center justify-center gap-2 group-hover:scale-105 transition-transform duration-200">
                      <Badge tone={(l.shortlistRate ?? 0) >= 0.25 ? "good" : (l.shortlistRate ?? 0) >= 0.15 ? "warn" : "bad"}>
                        {(l.shortlistRate != null ? (l.shortlistRate * 100).toFixed(1) : "0")}%
                      </Badge>
                    </div>
                  </td>
                </tr>
              ))}
              {!rows.length ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-500">
                    <div className="flex flex-col items-center gap-2">
                      <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center">
                        <Trophy className="h-6 w-6 text-slate-400" />
                      </div>
                      {loading ? (
                        <div className="space-y-2 w-full">
                          {Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="h-12 bg-slate-100 rounded-lg animate-pulse" />
                          ))}
                        </div>
                      ) : (
                        <span className="font-medium">No lenders match your filters.</span>
                      )}
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {/* Detailed Preview - Expandable row details */}
        {selected ? (
          <div className="mt-6 rounded-xl border-2 border-sky-300 bg-gradient-to-br from-sky-50 via-blue-50/50 to-white p-6 shadow-lg animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">{selected.lender} — Detailed Analytics</h3>
                <p className="text-xs text-slate-500 mt-1.5">Comprehensive metrics for selling analytics to this lender</p>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="p-2 rounded-lg hover:bg-sky-100 transition-all duration-200 hover:scale-110"
              >
                <X className="h-4 w-4 text-slate-500" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-sky-200 p-4 shadow-sm hover:shadow-md transition-all duration-200 hover:scale-105 hover:border-sky-300">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="h-4 w-4 text-sky-500" />
                  <div className="text-xs font-medium text-slate-600">Avg Time to Cash</div>
                </div>
                <div className="text-xl font-bold text-slate-900">{formatDays(selected.medianTtcDays || 0)}</div>
                <div className="text-xs text-slate-500 mt-1">Median days</div>
              </div>
              <div className="bg-white rounded-xl border border-emerald-200 p-4 shadow-sm hover:shadow-md transition-all duration-200 hover:scale-105 hover:border-emerald-300">
                <div className="flex items-center gap-2 mb-2">
                  <Package className="h-4 w-4 text-emerald-500" />
                  <div className="text-xs font-medium text-slate-600">Total Products</div>
                </div>
                <div className="text-xl font-bold text-slate-900">{formatInt(selected.portfolio?.activeProducts || 0)}</div>
                <div className="text-xs text-slate-500 mt-1">Active products</div>
              </div>
              <div className="bg-white rounded-xl border border-purple-200 p-4 shadow-sm hover:shadow-md transition-all duration-200 hover:scale-105 hover:border-purple-300">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="h-4 w-4 text-purple-500" />
                  <div className="text-xs font-medium text-slate-600">Total Views</div>
                </div>
                <div className="text-xl font-bold text-slate-900">{formatInt(selected.productViews || 0)}</div>
                <div className="text-xs text-slate-500 mt-1">Product views</div>
              </div>
              <div className="bg-white rounded-xl border border-amber-200 p-4 shadow-sm hover:shadow-md transition-all duration-200 hover:scale-105 hover:border-amber-300">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="h-4 w-4 text-amber-500" />
                  <div className="text-xs font-medium text-slate-600">Shortlisted %</div>
                </div>
                <div className="text-xl font-bold text-slate-900">{formatPct(selected.shortlistRate || 0, 1)}</div>
                <div className="text-xs text-slate-500 mt-1">Conversion rate</div>
              </div>
              <div className="bg-white rounded-xl border border-rose-200 p-4 shadow-sm hover:shadow-md transition-all duration-200 hover:scale-105 hover:border-rose-300">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="h-4 w-4 text-rose-500" />
                  <div className="text-xs font-medium text-slate-600">Approval Rate</div>
                </div>
                <div className="text-xl font-bold text-slate-900">{formatPct(selected.approvalRate || 0, 1)}</div>
                <div className="text-xs text-slate-500 mt-1">Application approval</div>
              </div>
              <div className="bg-white rounded-xl border border-indigo-200 p-4 shadow-sm hover:shadow-md transition-all duration-200 hover:scale-105 hover:border-indigo-300">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4 text-indigo-500" />
                  <div className="text-xs font-medium text-slate-600">Total Volume</div>
                </div>
                <div className="text-xl font-bold text-slate-900">{formatInt(selected.volume || 0)}</div>
                <div className="text-xs text-slate-500 mt-1">Applications</div>
              </div>
              <div className="bg-white rounded-xl border border-teal-200 p-4 shadow-sm hover:shadow-md transition-all duration-200 hover:scale-105 hover:border-teal-300">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="h-4 w-4 text-teal-500" />
                  <div className="text-xs font-medium text-slate-600">Avg Loan Amount</div>
                </div>
                <div className="text-xl font-bold text-slate-900">{formatGbp(selected.avgLoan || 0)}</div>
                <div className="text-xs text-slate-500 mt-1">Per application</div>
              </div>
              <div className="bg-white rounded-xl border border-cyan-200 p-4 shadow-sm hover:shadow-md transition-all duration-200 hover:scale-105 hover:border-cyan-300">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowRight className="h-4 w-4 text-cyan-500" />
                  <div className="text-xs font-medium text-slate-600">View → Apply</div>
                </div>
                <div className="text-xl font-bold text-slate-900">{formatPct(selected.viewToApply || 0, 1)}</div>
                <div className="text-xs text-slate-500 mt-1">Conversion rate</div>
              </div>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function ApplicationsSection({ data, loading, s, setS }) {
  // Mock data structure - replace with actual API data
  const applicationData = data?.applications || {};

  // 1. Application Volume KPIs
  const totalApplications = applicationData.totalApplications || data?.snapshot?.appsStarted || 0;
  // eslint-disable-next-line no-unused-vars
  const newToday = applicationData.newToday || 0;
  // eslint-disable-next-line no-unused-vars
  const newWeek = applicationData.newWeek || 0;
  // eslint-disable-next-line no-unused-vars
  const newMonth = applicationData.newMonth || 0;
  const activeApplications = applicationData.activeApplications || 0;
  const completedApplications = applicationData.completedApplications || data?.snapshot?.appsCompleted || 0;
  // eslint-disable-next-line no-unused-vars
  const droppedApplications = applicationData.droppedApplications || 0;
  const completionRate = safeDiv(completedApplications, totalApplications);
  const avgTimeToCompletion = applicationData.avgTimeToCompletion || data?.snapshot?.medianTtcDays || 0;

  // 2. Stage-wise Distribution
  const stageDistribution = applicationData.stageDistribution || {
    preEnquiry: 0,
    enquiry: 0,
    application: 0,
    approval: 0,
    valuation: 0,
    legals: 0,
    completion: 0,
  };

  // 3. Funnel Conversion Rates
  const funnelConversions = applicationData.funnelConversions || {
    preEnquiryToEnquiry: 0,
    enquiryToApplication: 0,
    applicationToApproval: 0,
    approvalToValuation: 0,
    valuationToLegals: 0,
    legalsToCompletion: 0,
  };

  // 4. Time-in-Stage Analytics
  const timeInStage = applicationData.timeInStage || {
    preEnquiryToEnquiry: 0,
    enquiryToApplication: 0,
    applicationToApproval: 0,
    approvalToValuation: 0,
    valuationToLegals: 0,
    legalsToCompletion: 0,
    totalEndToEnd: 0,
  };

  // 5. Stage Aging (Stuck Applications)
  // eslint-disable-next-line no-unused-vars
  const stuckApplications = applicationData.stuckApplications || {
    enquiry: 0,
    application: 0,
    approval: 0,
    valuation: 0,
    legals: 0,
  };

  // 6. Loan Purpose & Ownership Insights
  const loanPurposeInsights = applicationData.loanPurposeInsights || {
    purchase: { count: 0, completionRate: 0, avgTime: 0 },
    remortgage: { count: 0, completionRate: 0, avgTime: 0 },
    productTransfer: { count: 0, completionRate: 0, avgTime: 0 },
  };
  const ownershipInsights = applicationData.ownershipInsights || {
    individual: { count: 0, completionRate: 0, avgTime: 0 },
    partnership: { count: 0, completionRate: 0, avgTime: 0 },
    ltdCo: { count: 0, completionRate: 0, avgTime: 0 },
    pensionTrust: { count: 0, completionRate: 0, avgTime: 0 },
  };

  // Combine pension and trust if they exist separately (for backward compatibility)
  if (applicationData.ownershipInsights) {
    if (applicationData.ownershipInsights.pension || applicationData.ownershipInsights.trust) {
      const pension = applicationData.ownershipInsights.pension || { count: 0, completionRate: 0, avgTime: 0 };
      const trust = applicationData.ownershipInsights.trust || { count: 0, completionRate: 0, avgTime: 0 };
      const totalCount = (pension.count || 0) + (trust.count || 0);
      const totalCompleted = (pension.count || 0) * (pension.completionRate || 0) + (trust.count || 0) * (trust.completionRate || 0);
      const totalTime = (pension.count || 0) * (pension.avgTime || 0) + (trust.count || 0) * (trust.avgTime || 0);

      ownershipInsights.pensionTrust = {
        count: totalCount,
        completionRate: totalCount > 0 ? totalCompleted / totalCount : 0,
        avgTime: totalCount > 0 ? totalTime / totalCount : 0,
      };
    }
  }

  const stages = [
    { key: "preEnquiry", label: "Pre Enquiry", value: stageDistribution.preEnquiry },
    { key: "enquiry", label: "Enquiry", value: stageDistribution.enquiry },
    { key: "application", label: "Application", value: stageDistribution.application },
    { key: "approval", label: "Approval", value: stageDistribution.approval },
    { key: "valuation", label: "Valuation", value: stageDistribution.valuation },
    { key: "legals", label: "Legals", value: stageDistribution.legals },
    { key: "completion", label: "Completion", value: stageDistribution.completion },
  ];

  return (
    <div className="space-y-5">
      {/* Filters */}
      <section className="bg-white rounded-2xl border border-slate-200 p-4">
        <SectionHeader icon={BarChart3} title="Application Analytics" subtitle="Comprehensive application tracking, funnel analysis, and performance metrics." />
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 xl:grid-cols-12 gap-3">
          <div className="xl:col-span-3">
            <ControlSelect label="Date range" value={s.range} onChange={(v) => setS({ range: v })} options={RANGE_PRESETS} />
          </div>
          <div className="xl:col-span-3">
            <ControlSelect label="Product type" value={s.productType} onChange={(v) => setS({ productType: v })} options={PRODUCT_TYPES} />
          </div>
          <div className="xl:col-span-6">
            <SearchInput label="Search" value={s.q} onChange={(v) => setS({ q: v })} placeholder="Search lender / stage / product type…" />
          </div>
        </div>
      </section>

      {/* 1. Top KPIs - Enhanced for Admin & Lenders */}
      <section className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="h-5 w-5 text-sky-600" />
          <h2 className="text-lg font-semibold text-slate-900">Application Volume & Quality (Top KPIs)</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">Key metrics for platform health, admin workload, and lead quality insights for lenders</p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="group bg-gradient-to-br from-sky-50 to-sky-100 rounded-xl border border-sky-200 p-4 transition-all duration-300 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 hover:-translate-y-1">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="h-5 w-5 text-sky-600 transition-all duration-300 group-hover:scale-110" />
              <Badge tone="good">{formatInt(totalApplications)}</Badge>
            </div>
            <div className="text-xs font-medium text-sky-700 mb-1">Total Applications</div>
            <div className="text-xl font-bold text-sky-900 transition-all duration-300 group-hover:scale-105">{formatInt(totalApplications)}</div>
            <div className="mt-1 text-xs text-sky-600">All time</div>
          </div>
          <div className="group bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl border border-emerald-200 p-4 transition-all duration-300 hover:border-emerald-300 hover:shadow-lg hover:shadow-emerald-100 hover:-translate-y-1">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 transition-all duration-300 group-hover:scale-110" />
              <Badge tone={completionRate >= 0.3 ? "good" : completionRate >= 0.2 ? "warn" : "bad"}>{formatPct(completionRate, 1)}</Badge>
            </div>
            <div className="text-xs font-medium text-emerald-700 mb-1">Completion Rate</div>
            <div className="text-xl font-bold text-emerald-900 transition-all duration-300 group-hover:scale-105">{formatPct(completionRate, 1)}</div>
            <div className="mt-1 text-xs text-emerald-600">Quality indicator</div>
          </div>
          <div className="group bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl border border-amber-200 p-4 transition-all duration-300 hover:border-amber-300 hover:shadow-lg hover:shadow-amber-100 hover:-translate-y-1">
            <div className="flex items-center justify-between mb-2">
              <Clock className="h-5 w-5 text-amber-600 transition-all duration-300 group-hover:scale-110" />
              <Badge tone={avgTimeToCompletion <= 30 ? "good" : avgTimeToCompletion <= 45 ? "warn" : "bad"}>{formatDays(avgTimeToCompletion)}</Badge>
            </div>
            <div className="text-xs font-medium text-amber-700 mb-1">Avg Time to Completion</div>
            <div className="text-xl font-bold text-amber-900 transition-all duration-300 group-hover:scale-105">{formatDays(avgTimeToCompletion)}</div>
            <div className="mt-1 text-xs text-amber-600">SLA performance</div>
          </div>
          <div className="group bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200 p-4 transition-all duration-300 hover:border-purple-300 hover:shadow-lg hover:shadow-purple-100 hover:-translate-y-1">
            <div className="flex items-center justify-between mb-2">
              <Activity className="h-5 w-5 text-purple-600 transition-all duration-300 group-hover:scale-110" />
              <Badge tone="good">{formatInt(activeApplications)}</Badge>
            </div>
            <div className="text-xs font-medium text-purple-700 mb-1">Active Applications</div>
            <div className="text-xl font-bold text-purple-900 transition-all duration-300 group-hover:scale-105">{formatInt(activeApplications)}</div>
            <div className="mt-1 text-xs text-purple-600">In progress</div>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="h-5 w-5 text-indigo-600" />
            <h3 className="text-sm font-semibold text-slate-900">Lead Quality & Value Insights</h3>
          </div>
          <p className="text-xs text-slate-500 mb-4">Application value metrics and quality indicators for lender targeting</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="group/item p-4 bg-gradient-to-br from-sky-50 to-sky-100 rounded-lg border border-sky-200 transition-all duration-200 hover:bg-sky-100 hover:shadow-sm">
              <div className="text-xs font-medium text-sky-700 mb-1">Avg Loan Amount</div>
              <div className="text-xl font-bold text-sky-900 transition-all duration-200 group-hover/item:text-sky-700 group-hover/item:scale-105">{formatGbp(applicationData.avgLoanAmount || 185000)}</div>
              <div className="text-xs text-sky-600 mt-1">Per application</div>
            </div>
            <div className="group/item p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg border border-emerald-200 transition-all duration-200 hover:bg-emerald-100 hover:shadow-sm">
              <div className="text-xs font-medium text-emerald-700 mb-1">Total Portfolio Value</div>
              <div className="text-xl font-bold text-emerald-900 transition-all duration-200 group-hover/item:text-emerald-700 group-hover/item:scale-105">{formatGbp((applicationData.avgLoanAmount || 185000) * completedApplications)}</div>
              <div className="text-xs text-emerald-600 mt-1">Completed apps</div>
            </div>
            <div className="group/item p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg border border-amber-200 transition-all duration-200 hover:bg-amber-100 hover:shadow-sm">
              <div className="text-xs font-medium text-amber-700 mb-1">Avg LTV</div>
              <div className="text-xl font-bold text-amber-900 transition-all duration-200 group-hover/item:text-amber-700 group-hover/item:scale-105">{(applicationData.avgLTV || 72.5).toFixed(1)}%</div>
              <div className="text-xs text-amber-600 mt-1">Loan-to-value</div>
            </div>
            <div className="group/item p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200 transition-all duration-200 hover:bg-purple-100 hover:shadow-sm">
              <div className="text-xs font-medium text-purple-700 mb-1">Quality Score</div>
              <div className="text-xl font-bold text-purple-900 transition-all duration-200 group-hover/item:text-purple-700 group-hover/item:scale-105">{(applicationData.qualityScore || 78).toFixed(0)}/100</div>
              <div className="text-xs text-purple-600 mt-1">Based on completion</div>
            </div>
          </div>
        </div>
      </section>

      {/* Middle Section: Funnel + Time per Stage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Application Funnel */}
        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Target className="h-5 w-5 text-sky-600" />
            <h2 className="text-lg font-semibold text-slate-900">Application Funnel Conversion</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">Stage-to-stage conversion rates to identify drop-off points</p>

          <div className="space-y-3">
            <div className="text-xs font-semibold text-slate-700 mb-2">Stage Transition</div>
            <div className="space-y-2">
              <Row label="Pre Enquiry → Enquiry" value={formatPct(funnelConversions.preEnquiryToEnquiry, 1)} />
              <Row label="Enquiry → Application" value={formatPct(funnelConversions.enquiryToApplication, 1)} />
              <Row label="Application → Approval" value={formatPct(funnelConversions.applicationToApproval, 1)} />
              <Row label="Approval → Valuation" value={formatPct(funnelConversions.approvalToValuation, 1)} />
              <Row label="Valuation → Legals" value={formatPct(funnelConversions.valuationToLegals, 1)} />
              <Row label="Legals → Completion" value={formatPct(funnelConversions.legalsToCompletion, 1)} />
            </div>
          </div>
        </section>

        {/* Avg Time per Stage */}
        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Timer className="h-5 w-5 text-sky-600" />
            <h2 className="text-lg font-semibold text-slate-900">Time-in-Stage Analytics</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">Average time spent in each stage (days) for SLA monitoring</p>

          <div className="space-y-3">
            <Bars items={[
              { label: "Pre Enquiry → Enquiry", value: timeInStage.preEnquiryToEnquiry },
              { label: "Enquiry → Application", value: timeInStage.enquiryToApplication },
              { label: "Application → Approval", value: timeInStage.applicationToApproval },
              { label: "Approval → Valuation", value: timeInStage.approvalToValuation },
              { label: "Valuation → Legals", value: timeInStage.valuationToLegals },
              { label: "Legals → Completion", value: timeInStage.legalsToCompletion },
            ]} fmt={(v) => `${v.toFixed(1)}d`} />
            <div className="pt-2 border-t border-slate-200">
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold text-slate-900">Total end-to-end time</span>
                <span className="text-lg font-bold text-sky-600">{formatDays(timeInStage.totalEndToEnd)}</span>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Stage Distribution */}
      <section className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <PieChart className="h-5 w-5 text-sky-600" />
          <h2 className="text-lg font-semibold text-slate-900">Stage-wise Application Distribution</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">Number of applications currently in each stage</p>

        <div className="space-y-2">
          {stages.map((stage) => (
            <div key={stage.key} className="group flex items-center justify-between p-3 bg-slate-50 rounded-lg transition-all duration-300 hover:bg-sky-50 hover:shadow-sm hover:scale-[1.02] cursor-pointer border border-transparent hover:border-sky-200">
              <span className="text-sm font-medium text-slate-700 transition-colors duration-300 group-hover:text-slate-900">{stage.label}</span>
              <span className="text-lg font-bold text-slate-900 transition-all duration-300 group-hover:text-sky-600 group-hover:scale-110">{formatInt(stage.value)}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Additional Section */}
      <div className="grid grid-cols-1 gap-5">
        {/* Loan Purpose & Ownership Insights */}
        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Briefcase className="h-5 w-5 text-sky-600" />
            <h2 className="text-lg font-semibold text-slate-900">Loan Purpose & Ownership Insights</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">Breakdowns by loan purpose and ownership type</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-xs font-semibold text-slate-700 mb-2">Loan Purpose</div>
              <div className="space-y-2">
                <div className="group p-3 bg-slate-50 rounded-lg transition-all duration-300 hover:bg-sky-50 hover:shadow-sm hover:scale-[1.02] cursor-pointer border border-transparent hover:border-sky-200">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700 transition-colors duration-300 group-hover:text-slate-900">Purchase</span>
                    <span className="text-sm font-semibold text-slate-900 transition-all duration-300 group-hover:text-sky-600 group-hover:scale-110">{formatInt(loanPurposeInsights.purchase.count)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-600 transition-colors duration-300 group-hover:text-slate-700">
                    <span>Completion: {formatPct(loanPurposeInsights.purchase.completionRate, 1)}</span>
                    <span>Avg time: {formatDays(loanPurposeInsights.purchase.avgTime)}</span>
                  </div>
                </div>
                <div className="group p-3 bg-slate-50 rounded-lg transition-all duration-300 hover:bg-sky-50 hover:shadow-sm hover:scale-[1.02] cursor-pointer border border-transparent hover:border-sky-200">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700 transition-colors duration-300 group-hover:text-slate-900">Remortgage</span>
                    <span className="text-sm font-semibold text-slate-900 transition-all duration-300 group-hover:text-sky-600 group-hover:scale-110">{formatInt(loanPurposeInsights.remortgage.count)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-600 transition-colors duration-300 group-hover:text-slate-700">
                    <span>Completion: {formatPct(loanPurposeInsights.remortgage.completionRate, 1)}</span>
                    <span>Avg time: {formatDays(loanPurposeInsights.remortgage.avgTime)}</span>
                  </div>
                </div>
                <div className="group p-3 bg-slate-50 rounded-lg transition-all duration-300 hover:bg-sky-50 hover:shadow-sm hover:scale-[1.02] cursor-pointer border border-transparent hover:border-sky-200">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700 transition-colors duration-300 group-hover:text-slate-900">Product Transfer</span>
                    <span className="text-sm font-semibold text-slate-900 transition-all duration-300 group-hover:text-sky-600 group-hover:scale-110">{formatInt(loanPurposeInsights.productTransfer.count)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-600 transition-colors duration-300 group-hover:text-slate-700">
                    <span>Completion: {formatPct(loanPurposeInsights.productTransfer.completionRate, 1)}</span>
                    <span>Avg time: {formatDays(loanPurposeInsights.productTransfer.avgTime)}</span>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-700 mb-2">Ownership Type</div>
              <div className="space-y-2">
                <div className="group p-3 bg-slate-50 rounded-lg transition-all duration-300 hover:bg-sky-50 hover:shadow-sm hover:scale-[1.02] cursor-pointer border border-transparent hover:border-sky-200">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700 transition-colors duration-300 group-hover:text-slate-900">Individual</span>
                    <span className="text-sm font-semibold text-slate-900 transition-all duration-300 group-hover:text-sky-600 group-hover:scale-110">{formatInt(ownershipInsights.individual.count)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-600 transition-colors duration-300 group-hover:text-slate-700">
                    <span>Completion: {formatPct(ownershipInsights.individual.completionRate, 1)}</span>
                    <span>Avg time: {formatDays(ownershipInsights.individual.avgTime)}</span>
                  </div>
                </div>
                <div className="group p-3 bg-slate-50 rounded-lg transition-all duration-300 hover:bg-sky-50 hover:shadow-sm hover:scale-[1.02] cursor-pointer border border-transparent hover:border-sky-200">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700 transition-colors duration-300 group-hover:text-slate-900">Partnership</span>
                    <span className="text-sm font-semibold text-slate-900 transition-all duration-300 group-hover:text-sky-600 group-hover:scale-110">{formatInt(ownershipInsights.partnership.count)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-600 transition-colors duration-300 group-hover:text-slate-700">
                    <span>Completion: {formatPct(ownershipInsights.partnership.completionRate, 1)}</span>
                    <span>Avg time: {formatDays(ownershipInsights.partnership.avgTime)}</span>
                  </div>
                </div>
                <div className="group p-3 bg-slate-50 rounded-lg transition-all duration-300 hover:bg-sky-50 hover:shadow-sm hover:scale-[1.02] cursor-pointer border border-transparent hover:border-sky-200">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700 transition-colors duration-300 group-hover:text-slate-900">Ltd Co.</span>
                    <span className="text-sm font-semibold text-slate-900 transition-all duration-300 group-hover:text-sky-600 group-hover:scale-110">{formatInt(ownershipInsights.ltdCo.count)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-600 transition-colors duration-300 group-hover:text-slate-700">
                    <span>Completion: {formatPct(ownershipInsights.ltdCo.completionRate, 1)}</span>
                    <span>Avg time: {formatDays(ownershipInsights.ltdCo.avgTime)}</span>
                  </div>
                </div>
                <div className="group p-3 bg-slate-50 rounded-lg transition-all duration-300 hover:bg-sky-50 hover:shadow-sm hover:scale-[1.02] cursor-pointer border border-transparent hover:border-sky-200">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700 transition-colors duration-300 group-hover:text-slate-900">Pension/Trust</span>
                    <span className="text-sm font-semibold text-slate-900 transition-all duration-300 group-hover:text-sky-600 group-hover:scale-110">{formatInt(ownershipInsights.pensionTrust.count)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-600 transition-colors duration-300 group-hover:text-slate-700">
                    <span>Completion: {formatPct(ownershipInsights.pensionTrust.completionRate, 1)}</span>
                    <span>Avg time: {formatDays(ownershipInsights.pensionTrust.avgTime)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Product Category Performance in Applications - Sellable to Lenders */}
      <section className="group bg-white rounded-2xl border border-slate-200 p-6 transition-all duration-300 hover:border-teal-300 hover:shadow-lg hover:shadow-teal-100 hover:-translate-y-1">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-teal-50 to-teal-100 flex items-center justify-center transition-all duration-300 group-hover:from-teal-100 group-hover:to-teal-200 group-hover:scale-110">
            <Layers className="h-5 w-5 text-teal-600 transition-all duration-300 group-hover:text-teal-700 group-hover:scale-110" />
          </div>
          <h2 className="text-lg font-semibold text-slate-900 transition-colors duration-300 group-hover:text-teal-700">Product Category Performance</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">Application performance by product category - valuable for lender product strategy</p>
        <div className="space-y-2 mb-4">
          <Bars items={Object.entries(applicationData.byProductCategory || {
            BTL: { count: 520, completionRate: 0.28, avgLoan: 185000 },
            IF: { count: 180, completionRate: 0.23, avgLoan: 125000 },
            SC: { count: 85, completionRate: 0.21, avgLoan: 245000 },
            C: { count: 45, completionRate: 0.27, avgLoan: 320000 },
            Dev: { count: 35, completionRate: 0.26, avgLoan: 450000 },
          }).map(([category, data]) => ({
            label: category,
            value: data.count || 0,
          }))} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {Object.entries(applicationData.byProductCategory || {
            BTL: { count: 520, completionRate: 0.28, avgLoan: 185000 },
            IF: { count: 180, completionRate: 0.23, avgLoan: 125000 },
            SC: { count: 85, completionRate: 0.21, avgLoan: 245000 },
            C: { count: 45, completionRate: 0.27, avgLoan: 320000 },
            Dev: { count: 35, completionRate: 0.26, avgLoan: 450000 },
          }).map(([category, data]) => {
            const categoryColors = {
              BTL: { bg: "from-sky-50 to-sky-100", border: "border-sky-200", text: "text-sky-700", textBold: "text-sky-900", accent: "text-sky-600" },
              IF: { bg: "from-emerald-50 to-emerald-100", border: "border-emerald-200", text: "text-emerald-700", textBold: "text-emerald-900", accent: "text-emerald-600" },
              SC: { bg: "from-amber-50 to-amber-100", border: "border-amber-200", text: "text-amber-700", textBold: "text-amber-900", accent: "text-amber-600" },
              C: { bg: "from-purple-50 to-purple-100", border: "border-purple-200", text: "text-purple-700", textBold: "text-purple-900", accent: "text-purple-600" },
              Dev: { bg: "from-rose-50 to-rose-100", border: "border-rose-200", text: "text-rose-700", textBold: "text-rose-900", accent: "text-rose-600" },
            };
            const colors = categoryColors[category] || categoryColors.BTL;
            return (
              <div key={category} className={`group/item p-4 bg-gradient-to-br ${colors.bg} rounded-lg border ${colors.border} transition-all duration-200 hover:shadow-sm`}>
                <div className={`text-sm font-semibold mb-2 ${colors.textBold}`}>{category}</div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between">
                    <span className={colors.text}>Applications:</span>
                    <span className={`font-bold ${colors.textBold} transition-all duration-200 group-hover/item:${colors.accent} group-hover/item:scale-105`}>{formatInt(data.count || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={colors.text}>Completion:</span>
                    <span className={`font-bold ${colors.accent} transition-all duration-200 group-hover/item:scale-105`}>{formatPct(data.completionRate || 0, 1)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={colors.text}>Avg loan:</span>
                    <span className={`font-bold ${colors.textBold} transition-all duration-200 group-hover/item:${colors.accent} group-hover/item:scale-105`}>{formatGbp(data.avgLoan || 0)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {!data && loading ? (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="space-y-3 animate-pulse">
              <div className="h-4 bg-slate-200 rounded w-1/4" />
              <div className="h-4 bg-slate-200 rounded w-1/2" />
              <div className="h-4 bg-slate-200 rounded w-1/3" />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SourcingSection({ data, loading, s, setS }) {
  // Get filter analytics from data (loaded from localStorage) - only real data, no fallback
  const filterAnalytics = data?.sourcingFilters || {};
  
  console.log("SourcingSection - Filter Analytics:", {
    hasRealData: Object.keys(filterAnalytics).length > 0,
    realDataKeys: Object.keys(filterAnalytics),
    productTypeData: filterAnalytics.productType,
    sampleData: Object.keys(filterAnalytics).slice(0, 5).reduce((acc, key) => {
      acc[key] = filterAnalytics[key];
      return acc;
    }, {}),
  });

  // Calculate KPIs from filter analytics data
  const calculateTotalFilterUsage = () => {
    let total = 0;
    Object.values(filterAnalytics).forEach((filterData) => {
      if (filterData && typeof filterData === "object") {
        Object.values(filterData).forEach((value) => {
          total += Number(value) || 0;
        });
      }
    });
    return total;
  };

  const calculateTopFilterUsage = () => {
    let max = 0;
    Object.values(filterAnalytics).forEach((filterData) => {
      if (filterData && typeof filterData === "object") {
        Object.values(filterData).forEach((value) => {
          const num = Number(value) || 0;
          if (num > max) max = num;
        });
      }
    });
    return max;
  };

  const calculateUniqueFilterSelections = () => {
    let count = 0;
    Object.values(filterAnalytics).forEach((filterData) => {
      if (filterData && typeof filterData === "object") {
        count += Object.keys(filterData).length;
      }
    });
    return count;
  };

  const calculateAverageFilterUsage = () => {
    const total = calculateTotalFilterUsage();
    const selections = calculateUniqueFilterSelections();
    return selections > 0 ? total / selections : 0;
  };

  // Sourcing runs = total filter usage (each filter selection represents a sourcing run)
  const runs = calculateTotalFilterUsage();
  // Top filter uses = highest single filter value
  // eslint-disable-next-line no-unused-vars
  const topFilterUses = calculateTopFilterUsage();
  // Total filter selections = unique filter combinations
  // eslint-disable-next-line no-unused-vars
  const totalFilterSelections = calculateUniqueFilterSelections();
  // Average filter usage per selection
  // eslint-disable-next-line no-unused-vars
  const avgFilterUsage = calculateAverageFilterUsage();

  // Product views and conversion rates (keep from lender data if available, otherwise use approximations)
  // eslint-disable-next-line no-unused-vars
  const totalViews = data?.lenders?.length
    ? data.lenders.reduce((a, l) => a + (Number(l.productViews) || 0), 0)
    : Math.round(runs * 3.2); // Approximate: each sourcing run views ~3.2 products on average

  // eslint-disable-next-line no-unused-vars
  const avgViewToApply = data?.lenders?.length
    ? data.lenders.reduce((a, l) => a + (Number(l.viewToApply) || 0), 0) / data.lenders.length
    : 0.11; // Default 11% conversion

  // eslint-disable-next-line no-unused-vars
  const avgShortlist = data?.lenders?.length
    ? data.lenders.reduce((a, l) => a + (Number(l.shortlistRate) || 0), 0) / data.lenders.length
    : 0.26; // Default 26% shortlist rate

  const formatFilterData = (filterName, filterData) => {
    if (!filterData || typeof filterData !== "object") {
      console.log(`formatFilterData: No data for ${filterName}`, filterData);
      return [];
    }
    const formatted = Object.entries(filterData)
      .map(([key, value]) => ({ label: key, value: Number(value) || 0 }))
      .sort((a, b) => b.value - a.value);
    console.log(`formatFilterData for ${filterName}:`, formatted);
    return formatted;
  };

  // eslint-disable-next-line no-unused-vars
  const allFilterData = [
    ...formatFilterData("Repayment Type", filterAnalytics.repaymentType),
    ...formatFilterData("LTV", filterAnalytics.ltv),
    ...formatFilterData("EPC Rating", filterAnalytics.epcGreen),
    ...formatFilterData("Mortgage Term", filterAnalytics.mortgageTerm),
    ...formatFilterData("Product Term", filterAnalytics.productTerm),
    ...formatFilterData("Product Type", filterAnalytics.productType),
    ...formatFilterData("Borrower Type", filterAnalytics.borrowerType),
    ...formatFilterData("Home Owner", filterAnalytics.homeowner),
    ...formatFilterData("BTL Experience", filterAnalytics.btlExp),
    ...formatFilterData("Credit Worthiness", filterAnalytics.credit),
    ...formatFilterData("Tenancy Type", filterAnalytics.tenancyType),
    ...formatFilterData("Tenure", filterAnalytics.tenure),
    ...formatFilterData("Ex Local Authority", filterAnalytics.exAuthority),
    ...formatFilterData("Deposit Origination", filterAnalytics.depositOrigination),
    ...formatFilterData("Day 1 Remortgage", filterAnalytics.day1Remortgage),
    // Also include loan_purpose if it exists (mapped to loanPurpose)
    ...formatFilterData("Loan Purpose", filterAnalytics.loanPurpose),
  ].filter((x) => filterQuery(x.label, s.q));

  return (
    <div className="space-y-5">
      <section className="bg-white rounded-2xl border border-slate-200 p-4">
        <SectionHeader icon={Filter} title="Sourcing analytics" subtitle="Complete filter usage analytics mirroring all sourcing page filters + demand signals." />
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 xl:grid-cols-12 gap-3">
          <div className="xl:col-span-3">
            <ControlSelect label="Date range" value={s.range} onChange={(v) => setS({ range: v })} options={RANGE_PRESETS} />
          </div>
          <div className="xl:col-span-3">
            <ControlSelect label="Product type" value={s.productType} onChange={(v) => setS({ productType: v })} options={PRODUCT_TYPES} />
          </div>
          <div className="xl:col-span-6">
            <SearchInput label="Search filters" value={s.q} onChange={(v) => setS({ q: v })} placeholder="Search filter names/values…" />
          </div>
        </div>
      </section>

      {/* Filter Analytics by Category - Enhanced with hover effects */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Repayment Type */}
        <section className="group bg-white rounded-2xl border border-slate-200 p-6 transition-all duration-300 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 hover:-translate-y-1">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-sky-50 to-sky-100 flex items-center justify-center transition-all duration-300 group-hover:from-sky-100 group-hover:to-sky-200 group-hover:scale-110">
              <Filter className="h-5 w-5 text-sky-600 transition-all duration-300 group-hover:text-sky-700 group-hover:scale-110" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 transition-colors duration-300 group-hover:text-sky-700">Repayment Type</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">Usage distribution of repayment type filters</p>
          <Bars items={formatFilterData("", filterAnalytics.repaymentType)} />
        </section>

        {/* LTV */}
        <section className="group bg-white rounded-2xl border border-slate-200 p-6 transition-all duration-300 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 hover:-translate-y-1">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center transition-all duration-300 group-hover:from-emerald-100 group-hover:to-emerald-200 group-hover:scale-110">
              <TrendingUp className="h-5 w-5 text-emerald-600 transition-all duration-300 group-hover:text-emerald-700 group-hover:scale-110" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 transition-colors duration-300 group-hover:text-emerald-700">LTV Bands</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">Most searched LTV percentages</p>
          <Bars items={formatFilterData("", filterAnalytics.ltv)} />
        </section>

        {/* Mortgage Term */}
        <section className="group bg-white rounded-2xl border border-slate-200 p-6 transition-all duration-300 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 hover:-translate-y-1">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-amber-50 to-amber-100 flex items-center justify-center transition-all duration-300 group-hover:from-amber-100 group-hover:to-amber-200 group-hover:scale-110">
              <Clock className="h-5 w-5 text-amber-600 transition-all duration-300 group-hover:text-amber-700 group-hover:scale-110" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 transition-colors duration-300 group-hover:text-amber-700">Mortgage Term</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">Preferred mortgage term lengths</p>
          <Bars items={formatFilterData("", filterAnalytics.mortgageTerm)} />
        </section>

        {/* Product Term */}
        <section className="group bg-white rounded-2xl border border-slate-200 p-6 transition-all duration-300 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 hover:-translate-y-1">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 flex items-center justify-center transition-all duration-300 group-hover:from-purple-100 group-hover:to-purple-200 group-hover:scale-110">
              <Package className="h-5 w-5 text-purple-600 transition-all duration-300 group-hover:text-purple-700 group-hover:scale-110" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 transition-colors duration-300 group-hover:text-purple-700">Product Term</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">Product term preferences</p>
          <Bars items={formatFilterData("", filterAnalytics.productTerm)} />
        </section>

        {/* Product Type */}
        <section className="group bg-white rounded-2xl border border-slate-200 p-6 transition-all duration-300 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 hover:-translate-y-1">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-50 to-indigo-100 flex items-center justify-center transition-all duration-300 group-hover:from-indigo-100 group-hover:to-indigo-200 group-hover:scale-110">
              <Layers className="h-5 w-5 text-indigo-600 transition-all duration-300 group-hover:text-indigo-700 group-hover:scale-110" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 transition-colors duration-300 group-hover:text-indigo-700">Product Type</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">Rate type preferences (Fixed, Variable, etc.)</p>
          <Bars items={formatFilterData("", filterAnalytics.productType)} />
        </section>

        {/* Borrower Type */}
        <section className="group bg-white rounded-2xl border border-slate-200 p-6 transition-all duration-300 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 hover:-translate-y-1">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-rose-50 to-rose-100 flex items-center justify-center transition-all duration-300 group-hover:from-rose-100 group-hover:to-rose-200 group-hover:scale-110">
              <Users className="h-5 w-5 text-rose-600 transition-all duration-300 group-hover:text-rose-700 group-hover:scale-110" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 transition-colors duration-300 group-hover:text-rose-700">Borrower Type</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">Borrower entity type distribution</p>
          <Bars items={formatFilterData("", filterAnalytics.borrowerType)} />
        </section>

        {/* BTL Experience */}
        <section className="group bg-white rounded-2xl border border-slate-200 p-6 transition-all duration-300 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 hover:-translate-y-1">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-teal-50 to-teal-100 flex items-center justify-center transition-all duration-300 group-hover:from-teal-100 group-hover:to-teal-200 group-hover:scale-110">
              <Briefcase className="h-5 w-5 text-teal-600 transition-all duration-300 group-hover:text-teal-700 group-hover:scale-110" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 transition-colors duration-300 group-hover:text-teal-700">BTL Experience</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">Experience level distribution</p>
          <Bars items={formatFilterData("", filterAnalytics.btlExp)} />
        </section>

        {/* Credit Worthiness */}
        <section className="group bg-white rounded-2xl border border-slate-200 p-6 transition-all duration-300 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 hover:-translate-y-1">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center transition-all duration-300 group-hover:from-green-100 group-hover:to-green-200 group-hover:scale-110">
              <CheckCircle2 className="h-5 w-5 text-green-600 transition-all duration-300 group-hover:text-green-700 group-hover:scale-110" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 transition-colors duration-300 group-hover:text-green-700">Credit Worthiness</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">Credit score band usage</p>
          <Bars items={formatFilterData("", filterAnalytics.credit)} />
        </section>

        {/* EPC Rating */}
        <section className="group bg-white rounded-2xl border border-slate-200 p-6 transition-all duration-300 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 hover:-translate-y-1">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-cyan-50 to-cyan-100 flex items-center justify-center transition-all duration-300 group-hover:from-cyan-100 group-hover:to-cyan-200 group-hover:scale-110">
              <Activity className="h-5 w-5 text-cyan-600 transition-all duration-300 group-hover:text-cyan-700 group-hover:scale-110" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 transition-colors duration-300 group-hover:text-cyan-700">EPC Rating</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">Energy performance certificate rating usage</p>
          <Bars items={formatFilterData("", filterAnalytics.epcGreen)} />
        </section>

        {/* Loan Purpose */}
        {filterAnalytics.loanPurpose && (
          <section className="group bg-white rounded-2xl border border-slate-200 p-6 transition-all duration-300 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 hover:-translate-y-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-50 to-violet-100 flex items-center justify-center transition-all duration-300 group-hover:from-violet-100 group-hover:to-violet-200 group-hover:scale-110">
                <Target className="h-5 w-5 text-violet-600 transition-all duration-300 group-hover:text-violet-700 group-hover:scale-110" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900 transition-colors duration-300 group-hover:text-violet-700">Loan Purpose</h2>
            </div>
            <p className="text-xs text-slate-500 mb-4">Most searched loan purposes</p>
            <Bars items={formatFilterData("", filterAnalytics.loanPurpose)} />
          </section>
        )}

        {/* Home Owner */}
        <section className="group bg-white rounded-2xl border border-slate-200 p-6 transition-all duration-300 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 hover:-translate-y-1">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center transition-all duration-300 group-hover:from-emerald-100 group-hover:to-emerald-200 group-hover:scale-110">
              <Building2 className="h-5 w-5 text-emerald-600 transition-all duration-300 group-hover:text-emerald-700 group-hover:scale-110" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 transition-colors duration-300 group-hover:text-emerald-700">Home Owner</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">Home ownership status distribution</p>
          <Bars items={formatFilterData("", filterAnalytics.homeowner)} />
        </section>

        {/* Tenancy Type */}
        <section className="group bg-white rounded-2xl border border-slate-200 p-6 transition-all duration-300 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 hover:-translate-y-1">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-amber-50 to-amber-100 flex items-center justify-center transition-all duration-300 group-hover:from-amber-100 group-hover:to-amber-200 group-hover:scale-110">
              <FileText className="h-5 w-5 text-amber-600 transition-all duration-300 group-hover:text-amber-700 group-hover:scale-110" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 transition-colors duration-300 group-hover:text-amber-700">Tenancy Type</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">Tenancy agreement type preferences</p>
          <Bars items={formatFilterData("", filterAnalytics.tenancyType)} />
        </section>

        {/* Tenure */}
        <section className="group bg-white rounded-2xl border border-slate-200 p-6 transition-all duration-300 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 hover:-translate-y-1">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-50 to-violet-100 flex items-center justify-center transition-all duration-300 group-hover:from-violet-100 group-hover:to-violet-200 group-hover:scale-110">
              <Layers className="h-5 w-5 text-violet-600 transition-all duration-300 group-hover:text-violet-700 group-hover:scale-110" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 transition-colors duration-300 group-hover:text-violet-700">Tenure</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">Freehold vs leasehold property distribution</p>
          <Bars items={formatFilterData("", filterAnalytics.tenure)} />
        </section>

        {/* Ex Local Authority */}
        <section className="group bg-white rounded-2xl border border-slate-200 p-6 transition-all duration-300 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 hover:-translate-y-1">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-50 to-indigo-100 flex items-center justify-center transition-all duration-300 group-hover:from-indigo-100 group-hover:to-indigo-200 group-hover:scale-110">
              <Building2 className="h-5 w-5 text-indigo-600 transition-all duration-300 group-hover:text-indigo-700 group-hover:scale-110" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 transition-colors duration-300 group-hover:text-indigo-700">Ex Local Authority</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">Former local authority property status</p>
          <Bars items={formatFilterData("", filterAnalytics.exAuthority)} />
        </section>

        {/* Deposit Origination */}
        <section className="group bg-white rounded-2xl border border-slate-200 p-6 transition-all duration-300 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 hover:-translate-y-1">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-teal-50 to-teal-100 flex items-center justify-center transition-all duration-300 group-hover:from-teal-100 group-hover:to-teal-200 group-hover:scale-110">
              <Package className="h-5 w-5 text-teal-600 transition-all duration-300 group-hover:text-teal-700 group-hover:scale-110" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 transition-colors duration-300 group-hover:text-teal-700">Deposit Origination</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">Source of deposit funds distribution</p>
          <Bars items={formatFilterData("", filterAnalytics.depositOrigination)} />
        </section>

        {/* Day 1 Remortgage */}
        <section className="group bg-white rounded-2xl border border-slate-200 p-6 transition-all duration-300 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 hover:-translate-y-1">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-pink-50 to-pink-100 flex items-center justify-center transition-all duration-300 group-hover:from-pink-100 group-hover:to-pink-200 group-hover:scale-110">
              <RefreshCcw className="h-5 w-5 text-pink-600 transition-all duration-300 group-hover:text-pink-700 group-hover:scale-110" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 transition-colors duration-300 group-hover:text-pink-700">Day 1 Remortgage</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">Day 1 remortgage valuation type preferences</p>
          <Bars items={formatFilterData("", filterAnalytics.day1Remortgage)} />
        </section>
      </div>
    </div>
  );
}

function BorrowersSection({ data, loading, s, setS }) {
  const b = data?.borrowers || { active: 0, dormant: 0, newSignups: 0, activated: 0, repeatUsers: 0, satisfactionAvg: 0 };

  // Borrower portal analytics based on actual functionalities
  const portalAnalytics = data?.borrowerPortalAnalytics || {
    applications: {
      started: data?.snapshot?.appsStarted || 820,
      completed: data?.snapshot?.appsCompleted || 214,
      byBorrowerType: {
        individual: { started: 420, completed: 180 },
        partnership: { started: 210, completed: 95 },
        limited: { started: 150, completed: 65 },
        pension: { started: 40, completed: 18 },
      },
      byProductCategory: {
        BTL: { started: 520, completed: 145, completionRate: 0.28 },
        IF: { started: 180, completed: 42, completionRate: 0.23 },
        SC: { started: 85, completed: 18, completionRate: 0.21 },
        C: { started: 45, completed: 12, completionRate: 0.27 },
        Dev: { started: 35, completed: 9, completionRate: 0.26 },
      },
      avgTimeToComplete: 4.2, // days
      abandonmentRate: 0.74,
    },
    profile: {
      completed: 890,
      incomplete: 330,
      completionRate: 0.73,
    },
    engagement: {
      avgApplicationsPerBorrower: 1.8,
      borrowersWithMultipleApps: 420,
      avgShortlistSize: 2.8,
    },
  };

  const activationRate = safeDiv(b.activated, b.newSignups);
  const totalBorrowers = b.active + b.dormant;
  const activeRate = safeDiv(b.active, totalBorrowers);
  const repeatUserRate = safeDiv(b.repeatUsers, b.active);

  return (
    <div className="space-y-5">
      <section className="bg-white rounded-2xl border border-slate-200 p-4">
        <SectionHeader icon={Users} title="Borrower Analytics" subtitle="Borrower portal activity: signups, shortlisting, applications, profile completion & engagement (no personal/location data)." />
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 xl:grid-cols-12 gap-3">
          <div className="xl:col-span-3">
            <ControlSelect label="Date range" value={s.range} onChange={(v) => setS({ range: v })} options={RANGE_PRESETS} />
          </div>
          <div className="xl:col-span-9">
            <SearchInput label="Search" value={s.q} onChange={(v) => setS({ q: v })} placeholder="Search borrower activities…" />
          </div>
        </div>
      </section>

      {/* Top KPIs - Enhanced with sellable metrics */}
      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="group bg-gradient-to-br from-sky-50 to-sky-100 rounded-2xl border border-sky-200 p-5 transition-all duration-300 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 hover:-translate-y-1">
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center transition-all duration-300 group-hover:scale-110">
              <Users className="h-5 w-5 text-sky-600" />
            </div>
            <Badge tone="good">{formatPct(activeRate, 1)} active</Badge>
          </div>
          <div className="text-xs font-medium text-sky-700 mb-1">Total Borrowers</div>
          <div className="text-2xl font-bold text-sky-900 transition-all duration-300 group-hover:scale-105">{formatInt(totalBorrowers)}</div>
          <div className="mt-2 text-xs text-sky-600">
            <span className="font-semibold">{formatInt(b.active)}</span> active · <span className="font-semibold">{formatInt(b.dormant)}</span> dormant
          </div>
        </div>

        <div className="group bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl border border-emerald-200 p-5 transition-all duration-300 hover:border-emerald-300 hover:shadow-lg hover:shadow-emerald-100 hover:-translate-y-1">
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center transition-all duration-300 group-hover:scale-110">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
            </div>
            <Badge tone="good">{formatPct(activationRate, 1)}</Badge>
          </div>
          <div className="text-xs font-medium text-emerald-700 mb-1">Activation Rate</div>
          <div className="text-2xl font-bold text-emerald-900 transition-all duration-300 group-hover:scale-105">{formatInt(b.activated)}</div>
          <div className="mt-2 text-xs text-emerald-600">
            From <span className="font-semibold">{formatInt(b.newSignups)}</span> signups
          </div>
        </div>

        <div className="group bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl border border-amber-200 p-5 transition-all duration-300 hover:border-amber-300 hover:shadow-lg hover:shadow-amber-100 hover:-translate-y-1">
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center transition-all duration-300 group-hover:scale-110">
              <MousePointerClick className="h-5 w-5 text-amber-600" />
            </div>
            <Badge tone={repeatUserRate >= 0.3 ? "good" : "warn"}>{formatPct(repeatUserRate, 1)}</Badge>
          </div>
          <div className="text-xs font-medium text-amber-700 mb-1">Repeat Users</div>
          <div className="text-2xl font-bold text-amber-900 transition-all duration-300 group-hover:scale-105">{formatInt(b.repeatUsers)}</div>
          <div className="mt-2 text-xs text-amber-600">
            <span className="font-semibold">{formatPct(repeatUserRate, 1)}</span> of active users
          </div>
        </div>

        <div className="group bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl border border-purple-200 p-5 transition-all duration-300 hover:border-purple-300 hover:shadow-lg hover:shadow-purple-100 hover:-translate-y-1">
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center transition-all duration-300 group-hover:scale-110">
              <Sparkles className="h-5 w-5 text-purple-600" />
            </div>
            <Badge tone={b.satisfactionAvg >= 4.0 ? "good" : b.satisfactionAvg >= 3.5 ? "warn" : "bad"}>
              {b.satisfactionAvg ? `${b.satisfactionAvg.toFixed(1)}/5` : "—"}
            </Badge>
          </div>
          <div className="text-xs font-medium text-purple-700 mb-1">Satisfaction Score</div>
          <div className="text-2xl font-bold text-purple-900 transition-all duration-300 group-hover:scale-105">{b.satisfactionAvg ? `${b.satisfactionAvg.toFixed(1)}` : "—"}</div>
          <div className="mt-2 text-xs text-purple-600">Out of 5.0 rating</div>
        </div>
      </section>

      {/* Application Creation by Borrower Type */}
      <section className="group bg-white rounded-2xl border border-slate-200 p-6 transition-all duration-300 hover:border-emerald-300 hover:shadow-lg hover:shadow-emerald-100 hover:-translate-y-1">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center transition-all duration-300 group-hover:from-emerald-100 group-hover:to-emerald-200 group-hover:scale-110">
            <FileText className="h-5 w-5 text-emerald-600 transition-all duration-300 group-hover:text-emerald-700 group-hover:scale-110" />
          </div>
          <h2 className="text-lg font-semibold text-slate-900 transition-colors duration-300 group-hover:text-emerald-700">Application Creation by Borrower Type</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">Applications started and completed by borrower entity type</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(portalAnalytics.applications.byBorrowerType).map(([type, data]) => {
            const completionRate = safeDiv(data.completed, data.started);
            const typeLabel = type.charAt(0).toUpperCase() + type.slice(1).replace(/([A-Z])/g, " $1");
            return (
              <div key={type} className="group/item p-4 bg-slate-50 rounded-lg transition-all duration-200 hover:bg-emerald-50 hover:shadow-sm">
                <div className="text-xs font-semibold text-slate-700 mb-2">{typeLabel}</div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-600">Started:</span>
                    <span className="text-sm font-bold text-slate-900 transition-all duration-200 group-hover/item:text-emerald-600 group-hover/item:scale-105">{formatInt(data.started)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-600">Completed:</span>
                    <span className="text-sm font-bold text-slate-900 transition-all duration-200 group-hover/item:text-emerald-600 group-hover/item:scale-105">{formatInt(data.completed)}</span>
                  </div>
                  <div className="pt-1 border-t border-slate-200">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-slate-700">Completion:</span>
                      <span className="text-xs font-bold text-emerald-700 transition-all duration-200 group-hover/item:scale-105">{formatPct(completionRate, 1)}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Product Category Usage */}
      <section className="group bg-white rounded-2xl border border-slate-200 p-6 transition-all duration-300 hover:border-indigo-300 hover:shadow-lg hover:shadow-indigo-100 hover:-translate-y-1">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-50 to-indigo-100 flex items-center justify-center transition-all duration-300 group-hover:from-indigo-100 group-hover:to-indigo-200 group-hover:scale-110">
            <Layers className="h-5 w-5 text-indigo-600 transition-all duration-300 group-hover:text-indigo-700 group-hover:scale-110" />
          </div>
          <h2 className="text-lg font-semibold text-slate-900 transition-colors duration-300 group-hover:text-indigo-700">Product Category Usage</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">Most used product categories by borrowers (BTL, IF, SC, C, Dev)</p>
        <div className="space-y-2 mb-4">
          <Bars items={Object.entries(portalAnalytics.applications.byProductCategory || {}).map(([category, data]) => ({
            label: category,
            value: data.started,
          }))} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {Object.entries(portalAnalytics.applications.byProductCategory || {}).map(([category, data]) => {
            const categoryColors = {
              BTL: { bg: "from-sky-50 to-sky-100", border: "border-sky-200", hover: "hover:bg-sky-50", text: "text-sky-700", textBold: "text-sky-900", accent: "text-sky-600" },
              IF: { bg: "from-emerald-50 to-emerald-100", border: "border-emerald-200", hover: "hover:bg-emerald-50", text: "text-emerald-700", textBold: "text-emerald-900", accent: "text-emerald-600" },
              SC: { bg: "from-amber-50 to-amber-100", border: "border-amber-200", hover: "hover:bg-amber-50", text: "text-amber-700", textBold: "text-amber-900", accent: "text-amber-600" },
              C: { bg: "from-purple-50 to-purple-100", border: "border-purple-200", hover: "hover:bg-purple-50", text: "text-purple-700", textBold: "text-purple-900", accent: "text-purple-600" },
              Dev: { bg: "from-rose-50 to-rose-100", border: "border-rose-200", hover: "hover:bg-rose-50", text: "text-rose-700", textBold: "text-rose-900", accent: "text-rose-600" },
            };
            const colors = categoryColors[category] || categoryColors.BTL;
            const totalStarted = Object.values(portalAnalytics.applications.byProductCategory || {}).reduce((sum, d) => sum + (d.started || 0), 0);
            const percentage = totalStarted > 0 ? (data.started / totalStarted) * 100 : 0;
            return (
              <div key={category} className={`group/item p-4 bg-gradient-to-br ${colors.bg} rounded-lg border ${colors.border} transition-all duration-200 ${colors.hover} hover:shadow-sm`}>
                <div className="flex items-center justify-between mb-2">
                  <div className={`text-sm font-semibold ${colors.textBold}`}>{category}</div>
                  <Badge tone="good">{formatPct(percentage / 100, 1)}</Badge>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className={`text-xs ${colors.text}`}>Applications started:</span>
                    <span className={`text-sm font-bold ${colors.textBold} transition-all duration-200 group-hover/item:${colors.accent} group-hover/item:scale-105`}>{formatInt(data.started)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`text-xs ${colors.text}`}>Applications completed:</span>
                    <span className={`text-sm font-bold ${colors.textBold} transition-all duration-200 group-hover/item:${colors.accent} group-hover/item:scale-105`}>{formatInt(data.completed)}</span>
                  </div>
                  <div className="pt-1 border-t border-slate-200">
                    <div className="flex justify-between items-center">
                      <span className={`text-xs font-medium ${colors.text}`}>Completion rate:</span>
                      <span className={`text-xs font-bold ${colors.accent} transition-all duration-200 group-hover/item:scale-105`}>{formatPct(data.completionRate || safeDiv(data.completed, data.started), 1)}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Profile Completion */}
      <section className="group bg-white rounded-2xl border border-slate-200 p-6 transition-all duration-300 hover:border-amber-300 hover:shadow-lg hover:shadow-amber-100 hover:-translate-y-1">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-amber-50 to-amber-100 flex items-center justify-center transition-all duration-300 group-hover:from-amber-100 group-hover:to-amber-200 group-hover:scale-110">
            <User className="h-5 w-5 text-amber-600 transition-all duration-300 group-hover:text-amber-700 group-hover:scale-110" />
          </div>
          <h2 className="text-lg font-semibold text-slate-900 transition-colors duration-300 group-hover:text-amber-700">Profile Completion</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">Borrower profile completion status</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="group/item p-3 bg-slate-50 rounded-lg transition-all duration-200 hover:bg-amber-50 hover:shadow-sm">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-slate-700 transition-colors duration-200 group-hover/item:text-slate-900">Completed profiles</span>
              <span className="text-base font-bold text-slate-900 transition-all duration-200 group-hover/item:text-amber-600 group-hover/item:scale-105">{formatInt(portalAnalytics.profile.completed)}</span>
            </div>
          </div>
          <div className="group/item p-3 bg-slate-50 rounded-lg transition-all duration-200 hover:bg-amber-50 hover:shadow-sm">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-slate-700 transition-colors duration-200 group-hover/item:text-slate-900">Incomplete profiles</span>
              <span className="text-base font-bold text-slate-900 transition-all duration-200 group-hover/item:text-amber-600 group-hover/item:scale-105">{formatInt(portalAnalytics.profile.incomplete)}</span>
            </div>
          </div>
          <div className="group/item p-3 bg-emerald-50 rounded-lg border border-emerald-200 transition-all duration-200 hover:bg-emerald-100 hover:shadow-sm">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-emerald-700 transition-colors duration-200 group-hover/item:text-emerald-900">Completion rate</span>
              <span className="text-base font-bold text-emerald-700 transition-all duration-200 group-hover/item:text-emerald-800 group-hover/item:scale-105">{formatPct(portalAnalytics.profile.completionRate, 1)}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Activation Funnel - Enhanced */}
      <section className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-sky-50 to-sky-100 flex items-center justify-center">
            <Target className="h-5 w-5 text-sky-600" />
          </div>
          <h2 className="text-lg font-semibold text-slate-900">Activation Funnel</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">Borrower journey from signup to application completion</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="group bg-gradient-to-br from-sky-50 to-sky-100 rounded-xl border border-sky-200 p-4 transition-all duration-300 hover:border-sky-300 hover:shadow-md hover:shadow-sky-100 hover:-translate-y-0.5">
            <div className="text-xs font-semibold text-sky-700 mb-3">Signup → Activation</div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-sky-700">Signups</span>
                <span className="text-lg font-bold text-sky-900 transition-all duration-300 group-hover:scale-110">{formatInt(b.newSignups)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-sky-700">Activated</span>
                <span className="text-lg font-bold text-sky-900 transition-all duration-300 group-hover:scale-110">{formatInt(b.activated)}</span>
              </div>
              <div className="pt-2 border-t border-sky-200">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-semibold text-sky-800">Activation rate</span>
                  <span className="text-base font-bold text-sky-900 transition-all duration-300 group-hover:scale-110">{formatPct(activationRate, 1)}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="group bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl border border-emerald-200 p-4 transition-all duration-300 hover:border-emerald-300 hover:shadow-md hover:shadow-emerald-100 hover:-translate-y-0.5">
            <div className="text-xs font-semibold text-emerald-700 mb-3">Application Journey</div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-emerald-700">Apps started</span>
                <span className="text-lg font-bold text-emerald-900 transition-all duration-300 group-hover:scale-110">{formatInt(data?.snapshot?.appsStarted ?? 0)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-emerald-700">Apps completed</span>
                <span className="text-lg font-bold text-emerald-900 transition-all duration-300 group-hover:scale-110">{formatInt(data?.snapshot?.appsCompleted ?? 0)}</span>
              </div>
              <div className="pt-2 border-t border-emerald-200">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-semibold text-emerald-800">Completion rate</span>
                  <span className="text-base font-bold text-emerald-900 transition-all duration-300 group-hover:scale-110">{formatPct(safeDiv(data?.snapshot?.appsCompleted, data?.snapshot?.appsStarted), 1)}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="group bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl border border-amber-200 p-4 transition-all duration-300 hover:border-amber-300 hover:shadow-md hover:shadow-amber-100 hover:-translate-y-0.5">
            <div className="text-xs font-semibold text-amber-700 mb-3">Platform Health</div>
            <div className="space-y-2 text-xs text-amber-700">
              <div className="flex justify-between">
                <span>Total borrowers:</span>
                <span className="font-semibold text-amber-900">{formatInt(totalBorrowers)}</span>
              </div>
              <div className="flex justify-between">
                <span>Active rate:</span>
                <span className="font-semibold text-amber-900">{formatPct(activeRate, 1)}</span>
              </div>
              <div className="flex justify-between">
                <span>Repeat user rate:</span>
                <span className="font-semibold text-amber-900">{formatPct(repeatUserRate, 1)}</span>
              </div>
              <div className="pt-2 border-t border-amber-200 mt-2">
                <div className="text-[10px] text-amber-600 italic">
                  All metrics anonymized for privacy compliance
                </div>
              </div>
            </div>
          </div>
        </div>

        {!data && loading ? (
          <div className="mt-4 space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-12 bg-slate-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : null}
      </section>
    </div>
  );
}

function ClicksSection({ data, loading, s, setS }) {
  const c = data?.clicks || { borrowerClicks: 0, lenderClicks: 0, signups: 0, signins: 0, clickToSignup: 0 };
  const totalClicks = data?.snapshot?.clicks ?? 0;
  const borrowerClickRate = safeDiv(c.borrowerClicks, totalClicks);
  const lenderClickRate = safeDiv(c.lenderClicks, totalClicks);
  const totalRoleClicks = c.borrowerClicks + c.lenderClicks;
  // eslint-disable-next-line no-unused-vars
  const signupToSigninRate = safeDiv(c.signins, c.signups);

  return (
    <div className="space-y-5">
      <section className="bg-white rounded-2xl border border-slate-200 p-4">
        <SectionHeader icon={MousePointerClick} title="Clicks Analytics" subtitle="Site-level interaction metrics: homepage role selection, signup/signin funnel & conversion rates." />
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 xl:grid-cols-12 gap-3">
          <div className="xl:col-span-3">
            <ControlSelect label="Date range" value={s.range} onChange={(v) => setS({ range: v })} options={RANGE_PRESETS} />
          </div>
          <div className="xl:col-span-9">
            <SearchInput label="Search events" value={s.q} onChange={(v) => setS({ q: v })} placeholder="Search click events / funnels…" />
          </div>
        </div>
      </section>

      {/* Top KPIs - Enhanced with gradient cards */}
      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <div className="group bg-gradient-to-br from-sky-50 to-sky-100 rounded-2xl border border-sky-200 p-5 transition-all duration-300 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 hover:-translate-y-1">
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center transition-all duration-300 group-hover:scale-110">
              <MousePointerClick className="h-5 w-5 text-sky-600" />
            </div>
            <Badge tone="good">{formatInt(totalClicks)}</Badge>
          </div>
          <div className="text-xs font-medium text-sky-700 mb-1">Total Clicks</div>
          <div className="text-2xl font-bold text-sky-900 transition-all duration-300 group-hover:scale-105">{formatInt(totalClicks)}</div>
          <div className="mt-2 text-xs text-sky-600">All site interactions</div>
        </div>

        <div className="group bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl border border-emerald-200 p-5 transition-all duration-300 hover:border-emerald-300 hover:shadow-lg hover:shadow-emerald-100 hover:-translate-y-1">
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center transition-all duration-300 group-hover:scale-110">
              <Users className="h-5 w-5 text-emerald-600" />
            </div>
            <Badge tone="good">{formatPct(borrowerClickRate, 1)}</Badge>
          </div>
          <div className="text-xs font-medium text-emerald-700 mb-1">Borrower Clicks</div>
          <div className="text-2xl font-bold text-emerald-900 transition-all duration-300 group-hover:scale-105">{formatInt(c.borrowerClicks)}</div>
          <div className="mt-2 text-xs text-emerald-600">
            <span className="font-semibold">"I am a borrower"</span> selections
          </div>
        </div>

        <div className="group bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl border border-amber-200 p-5 transition-all duration-300 hover:border-amber-300 hover:shadow-lg hover:shadow-amber-100 hover:-translate-y-1">
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center transition-all duration-300 group-hover:scale-110">
              <Trophy className="h-5 w-5 text-amber-600" />
            </div>
            <Badge tone="good">{formatPct(lenderClickRate, 1)}</Badge>
          </div>
          <div className="text-xs font-medium text-amber-700 mb-1">Lender Clicks</div>
          <div className="text-2xl font-bold text-amber-900 transition-all duration-300 group-hover:scale-105">{formatInt(c.lenderClicks)}</div>
          <div className="mt-2 text-xs text-amber-600">
            <span className="font-semibold">"I am a lender"</span> selections
          </div>
        </div>

        <div className="group bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl border border-purple-200 p-5 transition-all duration-300 hover:border-purple-300 hover:shadow-lg hover:shadow-purple-100 hover:-translate-y-1">
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center transition-all duration-300 group-hover:scale-110">
              <TrendingUp className="h-5 w-5 text-purple-600" />
            </div>
            <Badge tone="good">{formatInt(c.signins)}</Badge>
          </div>
          <div className="text-xs font-medium text-purple-700 mb-1">Sign-ins</div>
          <div className="text-2xl font-bold text-purple-900 transition-all duration-300 group-hover:scale-105">{formatInt(c.signins)}</div>
          <div className="mt-2 text-xs text-purple-600">User logins</div>
        </div>

        <div className="group bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl border border-teal-200 p-5 transition-all duration-300 hover:border-teal-300 hover:shadow-lg hover:shadow-teal-100 hover:-translate-y-1">
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center transition-all duration-300 group-hover:scale-110">
              <CheckCircle2 className="h-5 w-5 text-teal-600" />
            </div>
            <Badge tone={c.clickToSignup >= 0.1 ? "good" : c.clickToSignup >= 0.05 ? "warn" : "bad"}>{formatPct(c.clickToSignup, 1)}</Badge>
          </div>
          <div className="text-xs font-medium text-teal-700 mb-1">Click → Signup</div>
          <div className="text-2xl font-bold text-teal-900 transition-all duration-300 group-hover:scale-105">{formatPct(c.clickToSignup, 1)}</div>
          <div className="mt-2 text-xs text-teal-600">Conversion rate</div>
        </div>
      </section>

      {/* Homepage Role Selection - Enhanced */}
      <section className="group bg-white rounded-2xl border border-slate-200 p-6 transition-all duration-300 hover:border-indigo-300 hover:shadow-lg hover:shadow-indigo-100 hover:-translate-y-1">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-50 to-indigo-100 flex items-center justify-center transition-all duration-300 group-hover:from-indigo-100 group-hover:to-indigo-200 group-hover:scale-110">
            <Target className="h-5 w-5 text-indigo-600 transition-all duration-300 group-hover:text-indigo-700 group-hover:scale-110" />
          </div>
          <h2 className="text-lg font-semibold text-slate-900 transition-colors duration-300 group-hover:text-indigo-700">Homepage Role Selection</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">User role selection clicks on homepage</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="group/item bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl border border-emerald-200 p-5 transition-all duration-300 hover:border-emerald-300 hover:shadow-md hover:shadow-emerald-100 hover:-translate-y-0.5">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-8 w-8 rounded-lg bg-white flex items-center justify-center transition-all duration-300 group-hover/item:scale-110">
                <Users className="h-4 w-4 text-emerald-600" />
              </div>
              <span className="text-sm font-semibold text-emerald-900">"I am a borrower"</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs text-emerald-700">Total clicks:</span>
                <span className="text-lg font-bold text-emerald-900 transition-all duration-300 group-hover/item:text-emerald-700 group-hover/item:scale-110">{formatInt(c.borrowerClicks)}</span>
              </div>
              <div className="pt-2 border-t border-emerald-200">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-medium text-emerald-700">Share of total:</span>
                  <span className="text-sm font-bold text-emerald-900 transition-all duration-300 group-hover/item:scale-110">{formatPct(borrowerClickRate, 1)}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="group/item bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl border border-amber-200 p-5 transition-all duration-300 hover:border-amber-300 hover:shadow-md hover:shadow-amber-100 hover:-translate-y-0.5">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-8 w-8 rounded-lg bg-white flex items-center justify-center transition-all duration-300 group-hover/item:scale-110">
                <Trophy className="h-4 w-4 text-amber-600" />
              </div>
              <span className="text-sm font-semibold text-amber-900">"I am a lender"</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs text-amber-700">Total clicks:</span>
                <span className="text-lg font-bold text-amber-900 transition-all duration-300 group-hover/item:text-amber-700 group-hover/item:scale-110">{formatInt(c.lenderClicks)}</span>
              </div>
              <div className="pt-2 border-t border-amber-200">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-medium text-amber-700">Share of total:</span>
                  <span className="text-sm font-bold text-amber-900 transition-all duration-300 group-hover/item:scale-110">{formatPct(lenderClickRate, 1)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4 p-4 bg-slate-50 rounded-xl">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-semibold text-slate-700">Role Selection Distribution</span>
            <span className="text-xs text-slate-600">{formatInt(totalRoleClicks)} total</span>
          </div>
          <div className="h-3 rounded-full bg-slate-200 overflow-hidden">
            <div className="h-3 flex">
              <div
                className="bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all duration-300 group-hover:from-emerald-500 group-hover:to-emerald-600"
                style={{ width: `${borrowerClickRate * 100}%` }}
              />
              <div
                className="bg-gradient-to-r from-amber-400 to-amber-500 transition-all duration-300 group-hover:from-amber-500 group-hover:to-amber-600"
                style={{ width: `${lenderClickRate * 100}%` }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Signup / Signin Funnel - Enhanced */}
      <section className="group bg-white rounded-2xl border border-slate-200 p-6 transition-all duration-300 hover:border-rose-300 hover:shadow-lg hover:shadow-rose-100 hover:-translate-y-1">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-rose-50 to-rose-100 flex items-center justify-center transition-all duration-300 group-hover:from-rose-100 group-hover:to-rose-200 group-hover:scale-110">
            <ArrowUpRight className="h-5 w-5 text-rose-600 transition-all duration-300 group-hover:text-rose-700 group-hover:scale-110" />
          </div>
          <h2 className="text-lg font-semibold text-slate-900 transition-colors duration-300 group-hover:text-rose-700">Signup / Signin Funnel</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">User conversion from clicks to signups and signins</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="group/item bg-gradient-to-br from-sky-50 to-sky-100 rounded-xl border border-sky-200 p-4 transition-all duration-300 hover:border-sky-300 hover:shadow-md hover:shadow-sky-100 hover:-translate-y-0.5">
            <div className="text-xs font-semibold text-sky-700 mb-3">New Sign-ups</div>
            <div className="text-2xl font-bold text-sky-900 transition-all duration-300 group-hover/item:scale-110">{formatInt(c.signups)}</div>
            <div className="mt-2 text-xs text-sky-600">New user registrations</div>
          </div>
          <div className="group/item bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200 p-4 transition-all duration-300 hover:border-purple-300 hover:shadow-md hover:shadow-purple-100 hover:-translate-y-0.5">
            <div className="text-xs font-semibold text-purple-700 mb-3">Sign-ins</div>
            <div className="text-2xl font-bold text-purple-900 transition-all duration-300 group-hover/item:scale-110">{formatInt(c.signins)}</div>
            <div className="mt-2 text-xs text-purple-600">User logins</div>
          </div>
          <div className="group/item bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl border border-teal-200 p-4 transition-all duration-300 hover:border-teal-300 hover:shadow-md hover:shadow-teal-100 hover:-translate-y-0.5">
            <div className="text-xs font-semibold text-teal-700 mb-3">Click → Signup</div>
            <div className="text-2xl font-bold text-teal-900 transition-all duration-300 group-hover/item:scale-110">{formatPct(c.clickToSignup, 1)}</div>
            <div className="mt-2 text-xs text-teal-600">Conversion rate</div>
          </div>
        </div>
        <div className="mt-4 p-4 bg-slate-50 rounded-xl">
          <div className="text-xs font-semibold text-slate-700 mb-3">Funnel Flow</div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <div className="flex justify-between text-xs text-slate-600 mb-1">
                  <span>Role Selection Clicks</span>
                  <span className="font-semibold">{formatInt(totalRoleClicks)}</span>
                </div>
                <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                  <div className="h-2 rounded-full bg-gradient-to-r from-sky-400 to-sky-500" style={{ width: "100%" }} />
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400" />
              <div className="flex-1">
                <div className="flex justify-between text-xs text-slate-600 mb-1">
                  <span>Sign-ups</span>
                  <span className="font-semibold">{formatInt(c.signups)}</span>
                </div>
                <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                  <div className="h-2 rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500" style={{ width: `${(c.signups / totalRoleClicks) * 100}%` }} />
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400" />
              <div className="flex-1">
                <div className="flex justify-between text-xs text-slate-600 mb-1">
                  <span>Sign-ins</span>
                  <span className="font-semibold">{formatInt(c.signins)}</span>
                </div>
                <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                  <div className="h-2 rounded-full bg-gradient-to-r from-purple-400 to-purple-500" style={{ width: `${(c.signins / totalRoleClicks) * 100}%` }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {!data && loading ? <div className="text-xs text-slate-500 text-center py-4">Loading click data…</div> : null}
    </div>
  );
}

/* =========================
   Small UI helpers
========================= */
// eslint-disable-next-line no-unused-vars
function Card({ children }) {
  return <div className="rounded-xl border border-slate-100 p-3">{children}</div>;
}
function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="text-xs text-slate-700 truncate">{label}</div>
      <div className="text-xs font-semibold text-slate-900">{value}</div>
    </div>
  );
}
// eslint-disable-next-line no-unused-vars
function Mini({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 p-3">
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="mt-1 text-sm font-semibold text-slate-900">{value}</div>
    </div>
  );
}
function Bars({ items = [], fmt }) {
  const max = Math.max(...items.map((i) => Number(i.value) || 0), 1);
  return (
    <div className="space-y-2">
      {items.map((i) => {
        const w = Math.round(((Number(i.value) || 0) / max) * 100);
        return (
          <div key={i.label} className="group space-y-1 transition-all duration-200 hover:bg-slate-50 -mx-1 px-1 py-0.5 rounded cursor-pointer">
            <div className="flex items-center justify-between gap-3">
              <div className="text-[11px] text-slate-600 truncate transition-colors duration-200 group-hover:text-slate-900">{i.label}</div>
              <div className="text-[11px] font-semibold text-slate-900 transition-all duration-200 group-hover:text-sky-600 group-hover:scale-105">
                {fmt ? fmt(Number(i.value) || 0) : formatInt(i.value)}
              </div>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden transition-all duration-200 group-hover:bg-slate-200">
              <div className="h-2 rounded-full bg-gradient-to-r from-sky-400 to-sky-500 transition-all duration-300 group-hover:from-sky-500 group-hover:to-sky-600 group-hover:shadow-sm" style={{ width: `${w}%` }} />
            </div>
          </div>
        );
      })}
      {!items.length ? <div className="text-xs text-slate-500">No data</div> : null}
    </div>
  );
}
// eslint-disable-next-line no-unused-vars
function FunnelBars({ steps = [] }) {
  const max = Math.max(...steps.map((s) => Number(s.value) || 0), 1);
  return (
    <div className="mt-3 space-y-2">
      {steps.map((s, idx) => {
        const w = Math.max(8, Math.round(((Number(s.value) || 0) / max) * 100));
        const next = steps[idx + 1]?.value ?? null;
        const conv = next != null ? safeDiv(next, s.value) : null;
        return (
          <div key={s.stage} className="space-y-1">
            <div className="flex items-center justify-between gap-3">
              <div className="text-[11px] text-slate-600 truncate">
                {s.stage} {conv != null ? <span className="text-slate-400">→ {formatPct(conv)}</span> : null}
              </div>
              <div className="text-[11px] font-semibold text-slate-900">{formatInt(s.value)}</div>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-2 rounded-full bg-sky-400" style={{ width: `${w}%` }} />
            </div>
          </div>
        );
      })}
      {!steps.length ? <div className="text-xs text-slate-500">No funnel data</div> : null}
    </div>
  );
}
function filterQuery(text, q) {
  const qq = (q || "").trim().toLowerCase();
  if (!qq) return true;
  return String(text || "").toLowerCase().includes(qq);
}
